import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, TrendingUp, Target, Filter, RefreshCw, Search,
  ChevronDown, ChevronUp, Sparkles, Mail, ExternalLink,
  AlertTriangle, Loader, BarChart2, UserPlus, LinkIcon,
  ShieldAlert, Activity, CheckCircle2, Clock, Info,
  Cpu, Database, Zap, LayoutDashboard, Building2, TrendingDown,
  HelpCircle, X, Scale,
} from 'lucide-react';
import api from '../services/api';
import AbaMalha from './radar/AbaMalha';
import AbaAlertas from './radar/AbaAlertas';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

// ── Higienização de tooltips ──────────────────────────────────────────────────
// O produto é white-label (N operadoras). Os nomes internos de tabela/view
// (crm.*, esau_*) e as referências a Oracle/FDW são específicos da instalação
// da Elosaúde — em outro cliente estariam FACTUALMENTE ERRADOS. Trocamos por
// termos neutros SEMPRE. Além disso, em modo demo escondemos a linha técnica
// "Fonte:" por completo (o prospect não precisa ver o encanamento).
const _NEUTRALIZAR = [
  [/crm\.radar_scores/g, 'base de scores do Radar'],
  [/crm\.radar_anomalias/g, 'base de anomalias do Radar'],
  [/crm\.radar_execucoes/g, 'registro de execuções do Radar'],
  [/crm\.esau_utilizacao/g, 'histórico de utilização'],
  [/crm\.beneficiarios/g, 'cadastro de beneficiários'],
  [/esau_v_fonte_item02/g, 'integração de utilização (ERP da operadora)'],
  [/esau_v_beneficiario/g, 'integração de cadastro (ERP da operadora)'],
  [/\s*\(FDW Oracle\)/g, ''],
  [/FDW Oracle/g, 'ERP da operadora'],
  [/\bFDW\b/g, 'ERP'],
  [/\bOracle\b/g, 'ERP'],
  // Nome do modelo de IA: white-label não expõe o fornecedor ("IA" basta).
  [/GPT-4o/gi, 'IA'],
  // varredura final: qualquer nome técnico residual que tenha escapado
  [/crm\.[a-z_]+/g, 'base interna do Radar'],
  [/esau_[a-z0-9_]+/g, 'integração do ERP'],
];

function sanitizarTooltip(text, demo) {
  if (!text) return text;
  let out = String(text).replace(/\\n/g, '\n');
  for (const [re, sub] of _NEUTRALIZAR) out = out.replace(re, sub);
  if (demo) {
    out = out
      .split('\n')
      .filter((l) => !l.trim().startsWith('Fonte:'))
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }
  return out;
}

// ── Tooltip ──────────────────────────────────────────────────────────────────
function Tip({ children, text, maxWidth = 340 }) {
  const { branding } = useBranding();
  const textoLimpo = sanitizarTooltip(text, branding?.demo);
  const [vis, setVis] = useState(false);
  const [pos, setPos] = useState({ top: true, left: '50%', tx: '-50%' });
  const ref = useRef(null);

  const handleEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceLeft = rect.left;
      const spaceRight = window.innerWidth - rect.right;
      const top = spaceAbove > 220;
      let left = '50%', tx = '-50%';
      if (spaceLeft < maxWidth / 2) { left = '0'; tx = '0'; }
      else if (spaceRight < maxWidth / 2) { left = 'auto'; tx = '0'; }
      setPos({ top, left, tx });
    }
    setVis(true);
  };

  // Renderiza o texto com suporte a seções (linhas começando com •, — ou chave:valor)
  const renderContent = (text) => {
    // Normaliza \n literais (caso venham como string escapada)
    const normalized = text.replace(/\\n/g, '\n');
    const lines = normalized.split('\n');
    return lines.map((line, i) => {
      if (!line.trim()) return <div key={i} style={{ height: 6 }} />;
      // Linha de fonte (começa com "Fonte:")
      if (line.startsWith('Fonte:')) return (
        <div key={i} style={{
          marginTop: 8, paddingTop: 8,
          borderTop: '1px solid rgba(32,164,144,0.25)',
          fontSize: '0.62rem', color: 'rgba(32,164,144,0.85)',
          fontFamily: 'monospace', letterSpacing: '0.02em',
        }}>{line}</div>
      );
      // Bullet points
      if (line.startsWith('•') || line.startsWith('—')) return (
        <div key={i} style={{ display: 'flex', gap: 6, marginTop: 3 }}>
          <span style={{ color: 'var(--brand, #20a490)', flexShrink: 0, marginTop: 1 }}>{line[0]}</span>
          <span style={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.55 }}>{line.slice(1).trim()}</span>
        </div>
      );
      // Linha numerada (1. 2. 3.)
      if (/^\d+\./.test(line)) return (
        <div key={i} style={{ display: 'flex', gap: 6, marginTop: 3 }}>
          <span style={{ color: 'var(--brand, #20a490)', flexShrink: 0, fontWeight: 700, minWidth: 16 }}>{line.match(/^\d+/)[0]}.</span>
          <span style={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.55 }}>{line.replace(/^\d+\./, '').trim()}</span>
        </div>
      );
      // Linha de destaque (toda em maiúsculo ou começa com ⚠/!)
      if (line.startsWith('Limitação:') || line.startsWith('Importante:') || line.startsWith('Atenção')) return (
        <div key={i} style={{
          marginTop: 6, padding: '4px 8px', borderRadius: 5,
          background: 'rgba(255,193,7,0.12)', borderLeft: '2px solid rgba(255,193,7,0.5)',
          color: 'rgba(255,220,100,0.95)', fontSize: '0.68rem', lineHeight: 1.5,
        }}>{line}</div>
      );
      // Texto normal
      return (
        <div key={i} style={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.6, marginTop: i === 0 ? 0 : 2 }}>
          {line}
        </div>
      );
    });
  };

  return (
    <span
      ref={ref}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'help' }}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setVis(false)}
    >
      {children}
      {vis && (
        <span style={{
          position: 'absolute',
          ...(pos.top ? { bottom: 'calc(100% + 10px)' } : { top: 'calc(100% + 10px)' }),
          left: pos.left === 'auto' ? undefined : pos.left,
          right: pos.left === 'auto' ? 0 : undefined,
          transform: pos.left === '50%' ? `translateX(${pos.tx})` : undefined,
          // Glassmorphism suave
          background: 'linear-gradient(160deg, rgba(18,52,46,0.88) 0%, rgba(12,38,33,0.92) 100%)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border: '1px solid rgba(32,164,144,0.18)',
          borderRadius: 10,
          padding: '12px 14px',
          maxWidth, width: 'max-content',
          fontSize: '0.71rem',
          zIndex: 9999,
          boxShadow: '0 6px 24px rgba(0,0,0,0.28), 0 1px 0 rgba(32,164,144,0.08) inset',
          pointerEvents: 'none',
        }}>
          {/* Seta */}
          <span style={{
            position: 'absolute',
            ...(pos.top ? { bottom: -6, top: undefined } : { top: -6, bottom: undefined }),
            left: pos.left === '50%' ? '50%' : pos.left === '0' ? 16 : undefined,
            right: pos.left === 'auto' ? 16 : undefined,
            transform: pos.left === '50%' ? 'translateX(-50%)' : undefined,
            width: 10, height: 6,
            overflow: 'hidden',
            display: 'block',
          }}>
            <span style={{
              display: 'block', width: 10, height: 10,
              background: 'rgba(15,55,48,0.97)',
              border: '1px solid rgba(32,164,144,0.30)',
              transform: pos.top ? 'rotate(45deg) translate(-3px,-3px)' : 'rotate(45deg) translate(3px,3px)',
            }} />
          </span>
          {renderContent(textoLimpo)}
        </span>
      )}
    </span>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const CORES_CLASS = {
  alto:  { bg: '#e8f5e9', color: '#2e7d32', label: 'Alto' },
  medio: { bg: '#fff8e1', color: '#f57f17', label: 'Médio' },
  baixo: { bg: '#fce4ec', color: '#c62828', label: 'Baixo' },
};

const LABEL_OPORT = {
  sem_agregado: {
    label: 'Sem agregado',
    color: '#1565c0', bg: '#e3f2fd',
    tip: 'Titular sem nenhum dependente ativo no mesmo contrato.\nFonte: esau_v_beneficiario (FDW Oracle)',
  },
  rf_indicavel: {
    label: 'RF indicável',
    color: '#6a1b9a', bg: '#f3e5f5',
    tip: 'Titular com contratos vinculados via matricula_titular. Pode indicar RF de outro contrato.\nFonte: esau_v_beneficiario (FDW Oracle)',
  },
  outros: {
    label: 'Outro',
    color: '#546e7a', bg: '#eceff1',
    tip: 'Sem oportunidade específica identificada.',
  },
};

const LABEL_PERFIL = {
  preventivo:         { label: 'Preventivo',         color: '#2e7d32', bg: '#e8f5e9' },
  agudo_eventual:     { label: 'Agudo eventual',     color: '#1565c0', bg: '#e3f2fd' },
  cronico_controlado: { label: 'Crônico controlado', color: '#e65100', bg: '#fff3e0' },
  alta_utilizacao:    { label: 'Alta utilização',    color: '#b71c1c', bg: '#ffebee' },
  sem_historico:      { label: 'Sem histórico',      color: '#546e7a', bg: '#eceff1' },
};

const CORES_NIVEL = {
  Alto:  { bg: '#ffebee', color: '#b71c1c', border: '#ef9a9a' },
  Médio: { bg: '#fff8e1', color: '#e65100', border: '#ffe082' },
  Baixo: { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
};

const LABEL_MODULO = {
  '2A': { label: '2A: Prestador',   tip: 'Volume de produção, superfaturamento, procedimentos impossíveis.\nFonte: agregação de cd_prestador em esau_v_fonte_item02' },
  '2B': { label: '2B: Beneficiário',tip: 'Cessão de carteirinha, inclusão oportunista, reembolsos suspeitos.\nFonte: matricula_benef + dt_realizado em esau_v_fonte_item02' },
  '2C': { label: '2C: Cadastro',    tip: 'Vínculo incompatível, ciclos de inclusão/exclusão, dados inconsistentes.\nFonte: crm.beneficiarios + esau_v_beneficiario' },
  '2D': { label: '2D: Conluio',     tip: 'Grupo de beneficiários distintos com padrão idêntico no mesmo prestador.\nFonte: esau_v_fonte_item02 cruzado entre beneficiários' },
};

const LABEL_CESTA = {
  'Preventivo':       { color: '#2e7d32', bg: '#e8f5e9', tip: 'Uso focado em prevenção: vacinas, check-ups, exames de rotina.' },
  'Crônico Estável':  { color: '#1565c0', bg: '#e3f2fd', tip: 'Doença crônica controlada com uso regular mas sem internações recentes.' },
  'Crônico Complexo': { color: '#e65100', bg: '#fff3e0', tip: 'Múltiplas condições crônicas com uso intenso e custo elevado.' },
  'Alto Custo':       { color: '#b71c1c', bg: '#ffebee', tip: 'Imunobiológicos, quimioterápicos ou internações de alta complexidade.' },
  'Uso Agudo Isolado':{ color: '#546e7a', bg: '#eceff1', tip: 'Uso pontual sem padrão crônico identificado.' },
};

function Pill({ label, color, bg, tip }) {
  const pill = (
    <span style={{
      fontSize: '0.65rem', fontWeight: 600, padding: '2px 8px',
      borderRadius: 10, background: bg, color, whiteSpace: 'nowrap',
    }}>{label}</span>
  );
  return tip ? <Tip text={tip}>{pill}</Tip> : pill;
}

function ScoreBadge({ score, classificacao, tip }) {
  const c = CORES_CLASS[classificacao] || CORES_CLASS.baixo;
  const badge = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 80, height: 8, borderRadius: 4, background: '#e0e0e0', overflow: 'hidden' }}>
        <div style={{ width: `${score}%`, height: '100%', borderRadius: 4, background: c.color }} />
      </div>
      <span style={{
        fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px',
        borderRadius: 10, background: c.bg, color: c.color, whiteSpace: 'nowrap',
      }}>
        {c.label} {score}
      </span>
    </div>
  );
  return tip ? <Tip text={tip}>{badge}</Tip> : badge;
}

function KpiCard({ icon: Icon, label, value, sub, color = '#1976d2', tip, onClick }) {
  const card = (
    <div onClick={onClick} style={{
      background: '#fff', borderRadius: 12, padding: '1.2rem 1.4rem',
      boxShadow: '0 1px 6px rgba(0,0,0,0.07)', flex: '1 1 180px', minWidth: 160,
      cursor: onClick ? 'pointer' : 'default', transition: 'box-shadow 0.15s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={18} color={color} />
        </div>
        <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', fontWeight: 500 }}>{label}</span>
        {tip && (
          <Tip text={tip}>
            <Info size={12} color="var(--gray-400)" style={{ marginLeft: 2 }} />
          </Tip>
        )}
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--gray-800)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
  return card;
}

// ── Abas ─────────────────────────────────────────────────────────────────────
const ABAS = [
  {
    id: 'visao-geral',
    label: 'Visão Geral',
    icon: LayoutDashboard,
    tip: 'Resumo executivo do Radar: distribuição de risco, anomalias abertas, top prestadores sinalizados e beneficiários críticos.',
  },
  {
    id: 'alertas',
    label: 'Alertas',
    icon: AlertTriangle,
    tip: 'Alertas de Cuidado: quem mudou de comportamento (gasto acelerou contra o próprio histórico, exame de investigação, medicamento de alto custo começou).\nPara o time agir antes de o caso virar problema financeiro. Aviso, não diagnóstico.',
  },
  {
    id: 'prospeccao',
    label: 'Prospecção',
    icon: UserPlus,
    tip: 'Titulares com maior propensão a incluir novos beneficiários.\nScore baseado em: tempo de carteira (crm.beneficiarios) + faixa etária + ausência de dependentes (esau_v_beneficiario)',
  },
  {
    id: 'sinistralidade',
    label: 'Risco de Sinistralidade',
    icon: Activity,
    tip: 'Score de risco calculado por GPT-4o com base na utilização real de cada beneficiário.\nFonte: esau_v_fonte_item02 (FDW Oracle), job noturno às 07h00 e 12h00',
  },
  {
    id: 'auditoria',
    label: 'Auditoria',
    icon: ShieldAlert,
    tip: 'Anomalias detectadas por GPT-4o: fraude de prestador (2A), beneficiário (2B), cadastro (2C), conluio (2D).\nFonte: esau_v_fonte_item02, job noturno às 07h00 e 12h00',
  },
  {
    id: 'malha',
    label: 'Malha',
    icon: Scale,
    tip: 'Réguas configuráveis (ex.: reembolso somado no mês acima de um limite) com Ação: setor obrigatório, tipo de tarefa, prioridade.\nO motor roda todo dia depois das contas médicas e abre tarefas no Kanban; na tarefa, o operador registra o parecer (justificado, irregular ou inconclusivo).',
  },
  {
    id: 'curva_doenca',
    label: 'Curva de Doença',
    icon: TrendingUp,
    tip: 'Early Warning: estratificação de risco com base no comportamento de utilização da massa completa da carteira.\nMetodologia: score composto por perfil clínico (categorias ativas), custo mensal, crescimento de custo e modelo ML preditivo.',
  },
  {
    id: 'escalonamento',
    label: 'Escalonamento',
    icon: Zap,
    tip: 'Vigilância precoce: beneficiários subindo a escada de investigação/tratamento numa linha de cuidado (renal, cardíaco, diabético), priorizados por urgência.\nSinal = padrão temporal do próprio beneficiário, não o custo. Watchlist, não diagnóstico.',
  },
  {
    id: 'investigacao',
    label: 'Investigação Ativa',
    icon: Target,
    tip: 'Quem entrou num workup diagnóstico sério (biópsia/PAAF, sedação para exame) sendo antes de baixa complexidade: o padrão "saudável de repente investigado pra algo sério".\nDetecção operacional pra coordenar o cuidado. Não prevê nem diagnostica.',
  },
  {
    id: 'saude_corporativa',
    label: 'Saúde Corporativa',
    icon: Building2,
    tip: 'Dashboard empresarial: curva de doença por empresa, padrões geográficos via prestador, concentração de doenças por região.',
  },
];

// ── AbaVisaoGeral ─────────────────────────────────────────────────────────────

const LABEL_MODULO_FULL = {
  '2A': { label: 'Fraude de Prestador',     cor: '#1565c0', bg: '#e3f2fd', tip: 'Volume de produção anômalo, superfaturamento ou procedimentos impossíveis por prestador.' },
  '2B': { label: 'Fraude de Beneficiário',  cor: '#6a1b9a', bg: '#f3e5f5', tip: 'Cessão de carteirinha, inclusão oportunista ou reembolsos suspeitos.' },
  '2C': { label: 'Inconsistência Cadastral',cor: '#e65100', bg: '#fff3e0', tip: 'Vínculo incompatível, ciclos de inclusão/exclusão ou dados inconsistentes.' },
  '2D': { label: 'Conluio',                 cor: '#b71c1c', bg: '#ffebee', tip: 'Grupo de beneficiários distintos com padrão idêntico no mesmo prestador.' },
};

function BarraHorizontal({ label, valor, total, cor, tip }) {
  const pct = total > 0 ? Math.round((valor / total) * 100) : 0;
  const barra = (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 4 }}>
        <span style={{ color: 'var(--gray-700)', fontWeight: 500 }}>{label}</span>
        <span style={{ color: 'var(--gray-500)' }}>{valor} <span style={{ color: 'var(--gray-400)' }}>({pct}%)</span></span>
      </div>
      <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: cor, borderRadius: 4, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
  return tip ? <Tip text={tip}>{barra}</Tip> : barra;
}

function MiniBarChart({ data, labelKey, valueKey, color = '#1976d2', height = 120 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height, paddingTop: 8 }}>
      {data.map((d, i) => {
        const val = d[valueKey] || 0;
        const pct = Math.max((val / max) * 100, 2);
        return (
          <Tip key={i} text={`${d[labelKey]}: ${typeof val === 'number' && val > 100 ? fmt(val) : val}`}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div style={{
                width: '100%', minWidth: 8, borderRadius: '3px 3px 0 0',
                background: color, height: `${pct}%`, transition: 'height 0.5s ease',
              }} />
              <span style={{ fontSize: '0.55rem', color: 'var(--gray-400)', transform: 'rotate(-45deg)', transformOrigin: 'center', whiteSpace: 'nowrap' }}>
                {d[labelKey]?.toString().slice(-5)}
              </span>
            </div>
          </Tip>
        );
      })}
    </div>
  );
}

function AbaVisaoGeral() {
  const [dados, setDados] = useState(null);
  const [pop, setPop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const [r1, r2] = await Promise.all([
        api.get('/radar-carteira/visao-geral'),
        api.get('/radar-carteira/populacao'),
      ]);
      setDados(r1.data);
      setPop(r2.data);
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao carregar visão geral');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-400)' }}>
      <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
      <div style={{ marginTop: 12 }}>Carregando visão gerencial...</div>
    </div>
  );

  if (erro) return (
    <div style={{ background: '#ffebee', borderRadius: 10, padding: '1rem', color: '#c62828' }}>{erro}</div>
  );

  if (!dados) return null;

  const { scores, cestas, anomalias, top_prestadores_sinalizados, top_alto_risco, data_ref, job_finalizado_em, total_ativos: totalAtivosVG } = dados;

  // Agrupar anomalias por módulo
  const anomPorModulo = {};
  (anomalias.por_modulo || []).forEach(({ modulo, nivel, qtd }) => {
    if (!anomPorModulo[modulo]) anomPorModulo[modulo] = { Alto: 0, Médio: 0, Baixo: 0, total: 0 };
    anomPorModulo[modulo][nivel] = (anomPorModulo[modulo][nivel] || 0) + qtd;
    anomPorModulo[modulo].total += qtd;
  });

  return (
    <div>
      {/* Cabeçalho da aba */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>
            Referência: <strong>{data_ref || '—'}</strong>
            {job_finalizado_em && ` · Atualizado: ${job_finalizado_em.slice(0,16).replace('T',' ')}`}
          </div>
        </div>
        <button onClick={carregar} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--gray-600)' }}>
          <RefreshCw size={13} /> Atualizar
        </button>
      </div>

      {/* KPIs de risco */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <KpiCard icon={Users} label="Analisados pelo job IA"
          value={`${scores.total}${totalAtivosVG ? ` / ${totalAtivosVG.toLocaleString('pt-BR')}` : ''}`}
          sub={totalAtivosVG ? `${Math.round((scores.total / totalAtivosVG) * 100)}% da carteira ativa` : 'beneficiários com score IA'}
          color="#546e7a"
          tip={`Quantos beneficiários foram processados pelo job IA no último ciclo vs total de ativos na carteira.\n\nComo funciona: o job roda às 18h30 e analisa todos os beneficiários com utilização nos últimos 12 meses via GPT-4o. Quem não tem utilização não recebe score.\n\nPor que pode ser menor que o total: beneficiários novos (sem histórico) ou com dados insuficientes não entram no processamento.\n\nFonte: crm.radar_scores (processados) · crm.beneficiarios (total ativos)`} />
        <KpiCard icon={AlertTriangle} label="Alto risco" value={scores.alto_risco} sub="score ≥ 8" color="#b71c1c"
          tip="Beneficiários com score de sinistralidade entre 8 e 10.\n\nComo o score é calculado: o GPT-4o avalia o histórico de utilização de cada beneficiário e atribui um score de 1 a 10 considerando: frequência de uso, complexidade dos procedimentos, custo acumulado, diagnósticos crônicos e padrão de escalada de cuidado.\n\nScore 8-10 = alto custo esperado, recomenda acionamento imediato do programa de gestão de doença.\n\nFonte: crm.radar_scores WHERE score >= 8" />
        <KpiCard icon={TrendingDown} label="Médio risco" value={scores.medio_risco} sub="score 5–7" color="#e65100"
          tip="Beneficiários com score entre 5 e 7: zona de atenção.\n\nEsses beneficiários apresentam padrão de uso relevante mas ainda não atingiram o limiar crítico. São candidatos ao monitoramento preventivo: uma mudança de comportamento nos próximos meses pode elevá-los para alto risco.\n\nRecomendação: acompanhar evolução mês a mês.\n\nFonte: crm.radar_scores WHERE score BETWEEN 5 AND 7" />
        <KpiCard icon={CheckCircle2} label="Baixo risco" value={scores.baixo_risco} sub="score < 5" color="#2e7d32"
          tip="Beneficiários com score abaixo de 5: perfil de baixo custo esperado.\n\nNão significa ausência de doença. Pode incluir beneficiários saudáveis ou com uso pontual (consultas, exames simples). O score reflete o padrão de custo, não diagnóstico clínico.\n\nFonte: crm.radar_scores WHERE score < 5" />
        {scores.sem_dados > 0 && (
          <KpiCard icon={Info} label="Sem score" value={scores.sem_dados} sub="score nulo" color="#78909c"
            tip="Beneficiários que passaram pelo job mas não receberam score.\n\nMotivos mais comuns: histórico de utilização muito curto (menos de 2 meses), dados inconsistentes na origem, ou timeout na chamada ao GPT-4o.\n\nEsses registros são incluídos na contagem de 'analisados' mas não aparecem nas faixas de risco. Revisar manualmente se o número for alto.\n\nFonte: crm.radar_scores WHERE score IS NULL" />
        )}
        <KpiCard icon={ShieldAlert} label="Anomalias abertas" value={anomalias.abertas ?? 0} sub={`${anomalias.resolvidas ?? 0} resolvidas`} color="#6a1b9a"
          tip="Quantidade de anomalias detectadas pelo job IA que ainda não foram marcadas como resolvidas.\n\nComo são detectadas: o job roda 6 módulos independentes de detecção (ver card 'Anomalias por Módulo'). Cada módulo compara padrões de uso com regras estatísticas e de negócio, e os desvios são registrados como anomalias.\n\nUma anomalia 'aberta' não significa fraude confirmada. É um sinal que requer investigação. O analista deve revisar e marcar como resolvida após análise.\n\nFonte: crm.radar_anomalias WHERE resolvida = FALSE" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>

        {/* Distribuição por cesta terapêutica */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.4rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <Tip text="Classificação do perfil terapêutico de cada beneficiário gerada pelo GPT-4o.\n\nComo funciona: o GPT-4o lê o histórico de procedimentos e medicamentos de cada beneficiário e o classifica em uma das cestas (ex: Alto Custo, Crônico, Agudo, Preventivo). A classificação reflete o padrão predominante de uso: um beneficiário pode ter usos mistos, mas recebe a cesta que melhor representa seu perfil.\n\nUso prático: cestas como 'Alto Custo' e 'Crônico' concentram o maior impacto financeiro e devem ser priorizadas nos programas de gestão.\n\nFonte: crm.radar_scores.cesta_terapeutica">
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--gray-700)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Activity size={14} color="var(--primary)" /> Cesta Terapêutica
            </div>
          </Tip>
          {cestas.length === 0 ? (
            <div style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}>Sem dados</div>
          ) : cestas.map(({ cesta, qtd }) => {
            const c = LABEL_CESTA[cesta] || { color: '#546e7a', bg: '#eceff1', tip: '' };
            return (
              <BarraHorizontal key={cesta} label={cesta} valor={qtd} total={scores.total} cor={c.color} tip={c.tip} />
            );
          })}
        </div>

        {/* Anomalias por módulo */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.4rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <Tip text="Anomalias abertas agrupadas por módulo de detecção.\n\nO job IA roda 6 módulos independentes:\n• 1A: frequência anormal de uso por beneficiário (ex: 40+ consultas/mês)\n• 1B: escalada de custo (beneficiário que acelerou gasto nos últimos 3 meses)\n• 2A: padrão suspeito de prestador (alto volume + baixa diversidade de diagnósticos)\n• 2B: beneficiário de alto custo concentrado em poucos prestadores\n• 3A: procedimentos incompatíveis (combinações improváveis de especialidades)\n• 3B: duplicidade (mesmo procedimento faturado múltiplas vezes no mesmo dia)\n\nNível Alto = requer investigação imediata · Médio = monitorar · Baixo = registro informativo.\n\nFonte: crm.radar_anomalias WHERE resolvida = FALSE">
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--gray-700)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <ShieldAlert size={14} color="#b71c1c" /> Anomalias por Módulo
            </div>
          </Tip>
          {Object.keys(LABEL_MODULO_FULL).map(mod => {
            const info = LABEL_MODULO_FULL[mod];
            const dados_mod = anomPorModulo[mod] || { Alto: 0, Médio: 0, Baixo: 0, total: 0 };
            if (dados_mod.total === 0 && !anomalias.por_modulo?.length) return null;
            return (
              <div key={mod} style={{ marginBottom: 14 }}>
                <Tip text={info.tip}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: info.bg, color: info.cor }}>{mod}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--gray-700)', fontWeight: 500 }}>{info.label}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-600)' }}>{dados_mod.total}</span>
                  </div>
                </Tip>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[['Alto', '#b71c1c', '#ffebee'], ['Médio', '#e65100', '#fff3e0'], ['Baixo', '#2e7d32', '#e8f5e9']].map(([nivel, cor, bg]) => (
                    <span key={nivel} style={{ fontSize: '0.65rem', padding: '2px 8px', borderRadius: 8, background: bg, color: cor, fontWeight: 600 }}>
                      {nivel}: {dados_mod[nivel] || 0}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

        {/* Top prestadores sinalizados (2A) */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.4rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <Tip text="Top 5 prestadores com mais anomalias abertas no módulo 2A.\n\nO módulo 2A detecta padrões suspeitos em prestadores, como:\n• Alto volume de atendimentos com pouca variação de procedimento (possível billing em lote)\n• Concentração de pacientes de alto custo em um único prestador\n• Faturamento acima da média para o mesmo tipo de procedimento\n\nImportante: sinalização não é confirmação de fraude. O próximo passo é o analista de auditoria revisar os prontuários e validar os itens faturados.\n\nFonte: crm.radar_anomalias WHERE modulo = '2A' AND resolvida = FALSE, ordenado por anomalias de nível Alto">
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--gray-700)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Building2 size={14} color="#1565c0" /> Top Prestadores Sinalizados (2A)
            </div>
          </Tip>
          {top_prestadores_sinalizados.length === 0 ? (
            <div style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}>Nenhum prestador sinalizado</div>
          ) : top_prestadores_sinalizados.map((p, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-800)' }}>{p.nm_prestador}</div>
                <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)' }}>Cód. {p.cd_prestador}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: '#ffebee', color: '#b71c1c' }}>
                  {p.anomalias_alto} alto · {p.ocorrencias} total
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Top beneficiários alto risco */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.4rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <Tip text="Top 10 beneficiários ATIVOS com score ≥ 8, ordenados por score decrescente.\n\nComo interpretar: cada card mostra nome, cesta terapêutica, recomendação do GPT-4o e score (1–10). A recomendação é gerada automaticamente com base no perfil de uso. Ex.: 'acionar programa de gestão de doença crônica' significa que o beneficiário tem padrão de tratamento contínuo e pode se beneficiar de acompanhamento coordenado.\n\nLimitação: o score reflete o último ciclo do job. Um beneficiário pode ter alta após a rodada e ainda aparecer aqui até o próximo processamento.\n\nSomente beneficiários com status ATIVO em crm.beneficiarios são exibidos; inativos são excluídos desta lista.\n\nFonte: crm.radar_scores JOIN crm.beneficiarios WHERE status = ATIVO AND score >= 8">
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--gray-700)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={14} color="#b71c1c" /> Beneficiários de Maior Risco
            </div>
          </Tip>
          {top_alto_risco.length === 0 ? (
            <div style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}>Nenhum beneficiário com score ≥ 8</div>
          ) : top_alto_risco.map((b, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-800)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {b.nome || b.matricula}
                </div>
                <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)' }}>
                  {b.cesta} · {b.recomendacao?.slice(0, 40)}{b.recomendacao?.length > 40 ? '…' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: '50%', background: '#ffebee', color: '#b71c1c', fontWeight: 800, fontSize: '0.85rem', flexShrink: 0, marginLeft: 10 }}>
                {b.score}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gráficos populacionais */}
      {pop && (
        <>
          {/* KPIs de carteira */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: '1.5rem', marginBottom: '1.5rem' }}>
            <KpiCard icon={Users} label="Vidas ativas" value={pop.total_ativos?.toLocaleString('pt-BR')} color="#1976d2"
              tip="Total de beneficiários com status ATIVO no cadastro.\n\nEste é o universo real da carteira: inclui quem usou e quem não usou o plano. É a base para calcular prevalência, custo per capita e cobertura do job IA.\n\nFonte: crm.beneficiarios WHERE status = 'ATIVO'" />
            <KpiCard icon={Activity} label="Com utilização" value={(pop.total_ativos - pop.sem_utilizacao)?.toLocaleString('pt-BR')}
              sub={`${Math.round(((pop.total_ativos - pop.sem_utilizacao) / pop.total_ativos) * 100)}% da carteira`}
              color="#2e7d32" tip="Beneficiários ativos que geraram pelo menos 1 evento de utilização nos últimos 12 meses.\n\nEste grupo é o universo do job IA: apenas quem tem utilização recebe score. Alta porcentagem de utilização pode indicar carteira mais onerosa; baixa pode indicar carteira jovem/saudável ou subnotificação.\n\nFonte: crm.beneficiarios INNER JOIN crm.esau_utilizacao (últimos 12 meses)" />
            <KpiCard icon={AlertTriangle} label="Sem utilização" value={pop.sem_utilizacao?.toLocaleString('pt-BR')}
              sub="potencial risco oculto" color="#e65100"
              tip="Beneficiários ativos sem nenhum registro de utilização nos últimos 12 meses.\n\nDuas interpretações possíveis:\n1. Beneficiário saudável: não precisou usar o plano (bom sinal)\n2. Risco oculto: usa particular, mudou de prestador, ou tem doença não declarada\n\nEste grupo NÃO recebe score IA. Recomenda-se monitorar concentrações por empresa (pode indicar subuso intencional ou perfil muito jovem).\n\nFonte: crm.beneficiarios WHERE NOT EXISTS (SELECT 1 FROM crm.esau_utilizacao)" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            {/* Histograma de scores */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '1.4rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <Tip text="Distribuição da carteira por faixa de score (1 a 10).\n\nComo ler: cada barra representa quantos beneficiários receberam aquele score no último job. Uma curva com concentração nos scores altos (8–10) indica carteira de alto custo; concentração em 1–4 indica carteira saudável.\n\nO ideal para uma operadora saudável é ter a maioria no score 1–4, poucos no 5–7 e um número controlável no 8–10 (onde o custo se concentra).\n\nFonte: crm.radar_scores, agrupado por score inteiro, último job">
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--gray-700)', marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <BarChart2 size={14} color="var(--primary)" /> Distribuição de Scores
              </div>
              </Tip>
              <MiniBarChart data={pop.histograma_scores} labelKey="score" valueKey="qtd" color="#1976d2" height={100} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: 'var(--gray-400)', marginTop: 4 }}>
                <span>1 (saudável)</span>
                <span>10 (alto risco)</span>
              </div>
            </div>

            {/* Custo mensal */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '1.4rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <Tip text="Custo total da carteira por mês (últimos meses disponíveis).\n\nComo ler: soma de todos os valores faturados em crm.esau_utilizacao agrupados por mês de competência. Inclui todos os beneficiários ativos com utilização: consultas, exames, internações, medicamentos.\n\nUso prático: identifica sazonalidade (picos em julho/dezembro são comuns por internações eletivas e férias) e tendência de crescimento da sinistralidade ao longo do tempo.\n\nFonte: crm.esau_utilizacao, SUM(valor_tot) GROUP BY mes_competencia">
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--gray-700)', marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <TrendingUp size={14} color="#2e7d32" /> Custo Mensal da Carteira
              </div>
              </Tip>
              <MiniBarChart data={pop.custo_mensal} labelKey="mes" valueKey="custo" color="#2e7d32" height={100} />
            </div>

            {/* Faixa etária */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '1.4rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <Tip text="Distribuição dos beneficiários ativos por faixa etária.\n\nPor que importa: faixa etária é um dos principais preditores de custo em saúde. Carteiras com alta concentração acima de 50 anos tendem a ter sinistralidade 3–5x maior que carteiras jovens.\n\nAtenção para a proporção 51–65 e 65+: se combinadas representarem mais de 25% da carteira, o risco atuarial é elevado e pode justificar revisão de precificação.\n\nFonte: crm.beneficiarios WHERE status = 'ATIVO', calculado pela data de nascimento">
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--gray-700)', marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Users size={14} color="#6a1b9a" /> Distribuição por Faixa Etária
              </div>
              </Tip>
              {pop.faixa_etaria.map(f => (
                <div key={f.faixa} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ width: 40, fontSize: '0.72rem', fontWeight: 600, color: 'var(--gray-600)' }}>{f.faixa}</span>
                  <div style={{ flex: 1, height: 8, background: '#f0f0f0', borderRadius: 4 }}>
                    <div style={{ height: '100%', width: `${Math.round((f.total / pop.total_ativos) * 100)}%`, background: '#7c4dff', borderRadius: 4 }} />
                  </div>
                  <span style={{ width: 50, textAlign: 'right', fontSize: '0.68rem', color: 'var(--gray-500)' }}>{f.total}</span>
                  <Tip text={`Score médio de risco IA para esta faixa\nFonte: crm.radar_scores`}>
                    <span style={{ width: 40, textAlign: 'right', fontSize: '0.68rem', fontWeight: 700,
                      color: f.score_medio >= 6 ? '#b71c1c' : f.score_medio >= 4 ? '#e65100' : '#2e7d32'
                    }}>{f.score_medio ?? '—'}</span>
                  </Tip>
                </div>
              ))}
            </div>

            {/* Top contratos por custo */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '1.4rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--gray-700)', marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Database size={14} color="#e65100" /> Top Empresas por Custo
              </div>
              {pop.top_contratos.map((c, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f5f5f5', fontSize: '0.72rem' }}>
                  <div>
                    <span style={{ fontWeight: 600, color: 'var(--gray-700)' }}>{c.empresa}</span>
                    <span style={{ color: 'var(--gray-400)', marginLeft: 8 }}>{c.vidas} vidas</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 700, color: 'var(--gray-700)' }}>{fmt(c.custo)}</span>
                    {c.score_medio && (
                      <span style={{
                        fontSize: '0.65rem', fontWeight: 700, padding: '2px 6px', borderRadius: 6,
                        background: c.score_medio >= 6 ? '#ffebee' : c.score_medio >= 4 ? '#fff3e0' : '#e8f5e9',
                        color: c.score_medio >= 6 ? '#b71c1c' : c.score_medio >= 4 ? '#e65100' : '#2e7d32',
                      }}>Risco {c.score_medio}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── AbaProspeccao ─────────────────────────────────────────────────────────────
function ProspectRow({ p, onVerPerfil }) {
  const [expanded, setExpanded] = useState(false);
  const [iaData, setIaData] = useState(null);
  const [iaLoading, setIaLoading] = useState(false);
  const [iaErro, setIaErro] = useState(null);

  const handleIA = async () => {
    if (!p.id_crm) return;
    if (iaData) { setExpanded(true); return; }
    setIaLoading(true);
    setExpanded(true);
    setIaErro(null);
    try {
      const r = await api.get(`/radar-carteira/beneficiario/${p.id_crm}/analise-ia?meses=12`);
      setIaData(r.data);
    } catch (e) {
      setIaErro(e.response?.data?.detail || 'Erro ao analisar');
    } finally {
      setIaLoading(false);
    }
  };

  const perfilInfo = iaData ? (LABEL_PERFIL[iaData.perfil_uso] || LABEL_PERFIL.sem_historico) : null;

  return (
    <>
      <tr style={{ borderBottom: '1px solid #f0f0f0' }}
          onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
          onMouseLeave={e => e.currentTarget.style.background = ''}>
        <td style={{ padding: '10px 12px', fontWeight: 600, fontSize: '0.85rem' }}>
          <div>{p.nome}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', fontWeight: 400 }}>
            <Tip text="Número do contrato (crm.beneficiarios.contrato)">
              <span>Contrato {p.contrato}</span>
            </Tip>
            {' · '}
            <Tip text={`Plano ativo (crm.beneficiarios.plano_contratado)`}>
              <span>{p.plano}</span>
            </Tip>
          </div>
        </td>
        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
          <ScoreBadge
            score={p.score_prospeccao}
            classificacao={p.classificacao_prospeccao}
            tip={`Score de prospecção (0-100)\n• Tempo de carteira: até 25pts (crm.beneficiarios.data_inscricao)\n• Faixa etária 25-45a: 15pts (crm.beneficiarios.data_nascimento)\n• Sem dependentes: 25pts (esau_v_beneficiario)\n\nClassificação: Alto ≥65 | Médio ≥40 | Baixo <40`}
          />
        </td>
        <td style={{ padding: '10px 12px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {p.oportunidades.map(o => {
              const info = LABEL_OPORT[o] || LABEL_OPORT.outros;
              return <Pill key={o} label={info.label} color={info.color} bg={info.bg} tip={info.tip} />;
            })}
          </div>
        </td>
        <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: '0.82rem' }}>
          <Tip text="Calculado a partir de crm.beneficiarios.data_nascimento">
            <span>{p.idade ? `${p.idade} anos` : '—'}</span>
          </Tip>
        </td>
        <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: '0.82rem' }}>
          <Tip text="Dependentes ativos no mesmo contrato\nFonte: esau_v_beneficiario.matricula_titular (FDW Oracle)">
            <span>{p.qtd_dependentes}</span>
          </Tip>
        </td>
        <td style={{ padding: '10px 12px' }}>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Tip text="Classifica os procedimentos deste beneficiário via GPT-4o\nFonte: esau_v_fonte_item02, últimos 12 meses">
              <button onClick={handleIA}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: iaData ? '#f3e5f5' : '#e8eaf6',
                  color: iaData ? '#6a1b9a' : '#3949ab',
                  fontSize: '0.72rem', fontWeight: 600,
                }}>
                <Sparkles size={12} />
                {iaLoading ? 'Analisando...' : 'IA'}
              </button>
            </Tip>
            {p.id_crm && (
              <Tip text="Abrir perfil completo do beneficiário">
                <button onClick={() => onVerPerfil(p)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    background: '#f5f5f5', color: '#424242', fontSize: '0.72rem', fontWeight: 600,
                  }}>
                  <ExternalLink size={12} /> Perfil
                </button>
              </Tip>
            )}
            <button onClick={() => setExpanded(v => !v)}
              style={{ padding: '4px 6px', borderRadius: 6, border: 'none', cursor: 'pointer', background: '#f5f5f5' }}>
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} style={{ padding: '0 12px 12px 24px', background: '#fafafa' }}>
            {iaLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#7c4dff', fontSize: '0.82rem', padding: '12px 0' }}>
                <Loader size={14} className="spin" />
                Buscando procedimentos e classificando com IA...
              </div>
            )}
            {iaErro && (
              <div style={{ color: '#c62828', fontSize: '0.82rem', padding: '8px 0' }}>
                <AlertTriangle size={14} style={{ marginRight: 6 }} />{iaErro}
              </div>
            )}
            {iaData && !iaLoading && (
              <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  {perfilInfo && <Pill label={perfilInfo.label} color={perfilInfo.color} bg={perfilInfo.bg}
                    tip={`Perfil de uso classificado por GPT-4o\nFonte: esau_v_fonte_item02, ${iaData.periodo_meses} meses`} />}
                  {iaData.flag_atencao && (
                    <Tip text={`Flag levantada por GPT-4o: ${iaData.motivo_flag}`}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        background: '#fff3e0', color: '#e65100', padding: '3px 10px',
                        borderRadius: 8, fontSize: '0.72rem', fontWeight: 600,
                      }}>
                        <AlertTriangle size={12} /> {iaData.motivo_flag || 'Atenção'}
                      </div>
                    </Tip>
                  )}
                  <Tip text="Total de eventos retornados por esau_v_fonte_item02">
                    <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>
                      {iaData.total_eventos} eventos · últimos {iaData.periodo_meses}m
                    </span>
                  </Tip>
                </div>
                {iaData.resumo && (
                  <p style={{ fontSize: '0.82rem', color: 'var(--gray-600)', margin: 0, fontStyle: 'italic' }}>
                    "{iaData.resumo}"
                  </p>
                )}
                {iaData.categorias && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {Object.entries(iaData.categorias).filter(([, v]) => v > 0)
                      .sort(([, a], [, b]) => b - a)
                      .map(([cat, qtd]) => (
                        <Tip key={cat} text={`Categoria classificada por GPT-4o\nFonte: descricao + procedimento de esau_v_fonte_item02`}>
                          <div style={{
                            background: '#fff', border: '1px solid #e0e0e0',
                            borderRadius: 8, padding: '4px 10px', fontSize: '0.72rem', color: 'var(--gray-600)',
                          }}>
                            {cat.replace(/_/g, ' ')} <strong>{qtd}</strong>
                          </div>
                        </Tip>
                      ))}
                  </div>
                )}
              </div>
            )}
            {!iaData && !iaLoading && !iaErro && (
              <p style={{ fontSize: '0.8rem', color: 'var(--gray-400)', padding: '8px 0', margin: 0 }}>
                Clique em <strong>IA</strong> para classificar os procedimentos.
              </p>
            )}
            <DetalheExpand matricula={p.matricula_soul} />
          </td>
        </tr>
      )}
    </>
  );
}

function AbaProspeccao({ dados, loading, erro, onRefresh, filtros, setFiltros }) {
  const navigate = useNavigate();
  const est = dados?.estatisticas || {};
  const prospects = dados?.prospects || [];

  const btnFiltro = (label, val, ativo, onClick) => (
    <button key={val} onClick={onClick} style={{
      padding: '5px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
      background: ativo ? 'var(--primary)' : '#f0f0f0',
      color: ativo ? '#fff' : 'var(--gray-600)',
      fontWeight: 600, fontSize: '0.78rem',
    }}>{label}</button>
  );

  return (
    <div>
      {/* KPIs */}
      {est.total > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: '1.2rem' }}>
          <KpiCard icon={Users} label="Total de Prospects" value={est.total} color="#1976d2"
            tip="Titulares ativos com score de prospecção calculado\nFonte: crm.beneficiarios WHERE tipo_beneficiario='TITULAR' AND status='ATIVO'" />
          <KpiCard icon={Target} label="Score Médio" value={`${est.score_medio}/100`} color="#7c4dff"
            tip="Média do score de prospecção da lista filtrada\nComposição: tempo de carteira + faixa etária + dependentes" />
          <KpiCard icon={UserPlus} label="Sem Agregado" value={est.sem_agregado} sub="oportunidade direta" color="#2e7d32"
            tip="Titulares sem nenhum dependente ativo\nFonte: esau_v_beneficiario.matricula_titular (FDW Oracle)" />
          <KpiCard icon={LinkIcon} label="RF Indicável" value={est.rf_indicavel} sub="contratos vinculados" color="#6a1b9a"
            tip="Titulares com múltiplos contratos vinculados\nFonte: esau_v_beneficiario, COUNT(DISTINCT contrato) > 1" />
        </div>
      )}

      {/* Filtros */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: '1rem 1.2rem',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '1rem',
        display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Filter size={14} color="var(--gray-400)" />
          <Tip text="Filtra por tipo de oportunidade identificada\nFonte: esau_v_beneficiario">
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)' }}>Oportunidade</span>
          </Tip>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[['Todos','todos'],['Sem agregado','sem_agregado'],['RF indicável','rf_indicavel']].map(([lbl,val]) =>
            btnFiltro(lbl, val, filtros.oport === val, () => setFiltros(f => ({ ...f, oport: val })))
          )}
        </div>
        <div style={{ width: 1, height: 20, background: '#e0e0e0' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <TrendingUp size={14} color="var(--gray-400)" />
          <Tip text="Classificação do score de prospecção\nAlto ≥65pts | Médio ≥40pts | Baixo <40pts">
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)' }}>Score</span>
          </Tip>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[['Todos',''],['Alto','alto'],['Médio','medio'],['Baixo','baixo']].map(([lbl,val]) =>
            btnFiltro(lbl, val, filtros.classificacao === val, () => setFiltros(f => ({ ...f, classificacao: val })))
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tip text="Limita quantos prospects são retornados pela API\nMáximo de 500, limitado a crm.beneficiarios TITULAR ATIVO">
            <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>Exibir</span>
          </Tip>
          <select value={filtros.limit} onChange={e => setFiltros(f => ({ ...f, limit: Number(e.target.value) }))}
            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e0e0e0', fontSize: '0.78rem' }}>
            {[50,100,200,500].map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>

      {erro && <ErroBanner msg={erro} />}
      {loading && !dados && <LoadingBanner msg="Calculando scores de prospecção…" sub="cadastro de beneficiários + integração do ERP" />}

      {!loading && prospects.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                <Th label="Titular" tip="Nome e contrato (crm.beneficiarios)" />
                <Th label="Score" center tip="Score de prospecção 0-100\ntempo + faixa etária + dependentes" />
                <Th label="Oportunidade" tip="Tipo de oportunidade (esau_v_beneficiario)" />
                <Th label="Idade" center tip="crm.beneficiarios.data_nascimento" />
                <Th label="Dependen." center tip="Dependentes ativos no contrato\nesau_v_beneficiario.matricula_titular" />
                <Th label="Ações" />
              </tr>
            </thead>
            <tbody>
              {prospects.map((p, i) => (
                <ProspectRow key={p.matricula_soul || i} p={p}
                  onVerPerfil={(p) => p.cpf && navigate(`/beneficiario-detalhe/${p.cpf}`)} />
              ))}
            </tbody>
          </table>
          <TableFooter count={prospects.length}
            note="Excluídos: titulares inativos. Score de sinistralidade disponível após job noturno às 07h00 e 12h00." />
        </div>
      )}
      {!loading && dados && prospects.length === 0 && <VazioMsg />}
    </div>
  );
}

// ── DetalheExpand (reutilizável) ──────────────────────────────────────────────
function DetalheExpand({ matricula }) {
  const [det, setDet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!matricula) return;
    setLoading(true);
    api.get(`/radar-carteira/beneficiario/${matricula}/detalhe-utilizacao`)
      .then(r => setDet(r.data))
      .catch(() => setDet(null))
      .finally(() => setLoading(false));
  }, [matricula]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--gray-400)', fontSize: '0.82rem', padding: '12px 0' }}>
      <Loader size={14} className="spin" /> Carregando detalhes...
    </div>
  );
  if (!det) return <div style={{ color: 'var(--gray-400)', fontSize: '0.82rem', padding: '8px 0' }}>Sem dados disponíveis</div>;

  return (
    <div style={{ padding: '12px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {/* Utilização mensal */}
      <div>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-500)', marginBottom: 8, textTransform: 'uppercase' }}>
          Utilização Mensal
        </div>
        {det.utilizacao_mensal.length === 0 ? (
          <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Sem utilização nos últimos 12 meses</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {det.utilizacao_mensal.map(m => {
              const maxCusto = Math.max(...det.utilizacao_mensal.map(x => x.custo), 1);
              const pct = Math.round((m.custo / maxCusto) * 100);
              return (
                <div key={m.mes} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.72rem' }}>
                  <span style={{ width: 50, color: 'var(--gray-500)', fontWeight: 500 }}>{m.mes.slice(5)}/{m.mes.slice(2,4)}</span>
                  <div style={{ flex: 1, height: 6, background: '#f0f0f0', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: m.internacoes > 0 ? '#b71c1c' : '#1976d2', borderRadius: 3 }} />
                  </div>
                  <span style={{ width: 70, textAlign: 'right', color: 'var(--gray-600)' }}>{fmt(m.custo)}</span>
                  <span style={{ width: 30, textAlign: 'right', color: 'var(--gray-400)' }}>{m.eventos}ev</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Top procedimentos */}
      <div>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-500)', marginBottom: 8, textTransform: 'uppercase' }}>
          Top Procedimentos
        </div>
        {det.top_procedimentos.length === 0 ? (
          <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Nenhum procedimento</div>
        ) : det.top_procedimentos.slice(0, 6).map((p, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', padding: '3px 0', borderBottom: '1px solid #f5f5f5' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: 'var(--gray-700)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.descricao}</div>
              <div style={{ color: 'var(--gray-400)', fontSize: '0.65rem' }}>{p.prestador}</div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
              <div style={{ color: 'var(--gray-600)', fontWeight: 600 }}>{fmt(p.custo)}</div>
              <div style={{ color: 'var(--gray-400)' }}>{p.qtd}x</div>
            </div>
          </div>
        ))}
      </div>

      {/* Prestadores */}
      {det.top_prestadores.length > 0 && (
        <div>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-500)', marginBottom: 8, textTransform: 'uppercase' }}>
            Prestadores
          </div>
          {det.top_prestadores.map((p, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', padding: '3px 0', borderBottom: '1px solid #f5f5f5' }}>
              <span style={{ color: 'var(--gray-700)' }}>{p.nm_prestador || `Cód. ${p.cd_prestador}`}</span>
              <span style={{ color: 'var(--gray-600)', fontWeight: 600 }}>{fmt(p.custo)} · {p.qtd} proc.</span>
            </div>
          ))}
        </div>
      )}

      {/* Anomalias abertas */}
      {det.anomalias_abertas.length > 0 && (
        <div>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#b71c1c', marginBottom: 8, textTransform: 'uppercase' }}>
            Anomalias Abertas
          </div>
          {det.anomalias_abertas.map((a, i) => (
            <div key={i} style={{ fontSize: '0.72rem', padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
              <span style={{ fontWeight: 700, color: CORES_NIVEL[a.nivel]?.color || '#424242' }}>{a.modulo} {a.nivel}</span>
              <span style={{ color: 'var(--gray-600)', marginLeft: 8 }}>{a.descricao}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Painel de Investigação Preditiva ────────────────────────────────────────
const CATEGORIAS_CLINICAS_CORES = {
  'Diabetes': '#e65100', 'Cardiovascular': '#b71c1c', 'Autoimune/Inflamatório': '#6a1b9a',
  'Renal': '#00695c', 'Tireoide': '#1565c0', 'Oncológico': '#880e4f',
  'Saúde Mental': '#4527a0', 'Dislipidemia': '#ef6c00', 'Internação/Urgência': '#c62828',
  'TEA/Neurodesenvolvimento': '#00838f', 'Fonoaudiologia': '#558b2f',
  'Fisioterapia/Reabilitação': '#2e7d32', 'Ortopédico/Coluna': '#5d4037',
  'Materno-Infantil': '#ad1457', 'Obesidade/Bariátrica': '#ff6f00',
};

function PainelInvestigacao({ matricula }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [abaAtiva, setAbaAtiva] = useState('resumo');
  const [timelineExpandida, setTimelineExpandida] = useState(false);

  const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;

  useEffect(() => {
    if (!matricula) return;
    setLoading(true);
    api.get(`/radar-carteira/beneficiario/${matricula}/investigacao-preditiva`)
      .then(r => setDados(r.data))
      .catch(() => setDados(null))
      .finally(() => setLoading(false));
  }, [matricula]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--gray-400)', fontSize: '0.82rem', padding: '12px 0' }}>
      <Loader size={14} className="spin" /> Analisando trajetória preditiva...
    </div>
  );
  if (!dados) return <DetalheExpand matricula={matricula} />;

  const { beneficiario: ben, resumo, timeline_clinica, categorias_emergentes, top_fatores_ml, monitorar_proximo, comparativo_custo, similaridade_transicao: sim, dependencia_funcional: depFunc } = dados;
  const abas = [
    { id: 'resumo', label: 'Resumo', icon: Target },
    { id: 'similaridade', label: 'Similaridade', icon: Cpu, badge: sim ? `${sim.score_similaridade}%` : null },
    { id: 'timeline', label: 'Timeline', icon: Activity },
    { id: 'categorias', label: 'Categorias', icon: ShieldAlert, count: categorias_emergentes.length },
    { id: 'monitorar', label: 'Monitorar', icon: AlertTriangle, count: monitorar_proximo.length },
  ];

  // ── Sub-abas ──
  const renderAbas = () => (
    <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
      {abas.map(a => {
        const Icon = a.icon;
        const ativo = abaAtiva === a.id;
        return (
          <button key={a.id} onClick={() => setAbaAtiva(a.id)} style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
            background: ativo ? 'var(--primary, #1565c0)' : '#f0f0f0',
            color: ativo ? '#fff' : 'var(--gray-600)',
            fontWeight: 600, fontSize: '0.72rem', transition: 'all 0.15s',
          }}>
            <Icon size={12} />
            {a.label}
            {a.count > 0 && (
              <span style={{
                background: ativo ? 'rgba(255,255,255,0.3)' : '#ddd',
                borderRadius: 8, padding: '0 5px', fontSize: '0.62rem',
              }}>{a.count}</span>
            )}
            {a.badge && (
              <span style={{
                background: ativo ? 'rgba(255,255,255,0.3)' : '#e8eaf6',
                borderRadius: 8, padding: '0 5px', fontSize: '0.62rem',
                fontWeight: 700, color: ativo ? '#fff' : '#283593',
              }}>{a.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );

  // ── Stepper de Trajetória ──
  const renderStepper = () => {
    const pos = resumo.posicao_trajetoria;
    if (!pos) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-500)', marginBottom: 10, textTransform: 'uppercase' }}>
          Posição na Trajetória Preditiva
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
          {pos.etapas.map((e, i) => (
            <React.Fragment key={e.numero}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 100 }}>
                <Tip text={e.descricao}>
                  <div style={{
                    width: e.atual ? 28 : 22, height: e.atual ? 28 : 22,
                    borderRadius: '50%',
                    background: e.ativa ? (e.atual ? '#1565c0' : '#64b5f6') : '#e0e0e0',
                    border: e.atual ? '3px solid #0d47a1' : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: e.ativa ? '#fff' : '#999', fontWeight: 700, fontSize: '0.65rem',
                    boxShadow: e.atual ? '0 0 0 4px rgba(21,101,192,0.2)' : 'none',
                    transition: 'all 0.2s',
                  }}>
                    {e.numero}
                  </div>
                </Tip>
                <div style={{
                  fontSize: '0.58rem', color: e.atual ? '#1565c0' : (e.ativa ? '#546e7a' : '#bbb'),
                  fontWeight: e.atual ? 700 : 400, textAlign: 'center', marginTop: 4,
                  lineHeight: 1.2, maxWidth: 90,
                }}>
                  {e.nome}
                </div>
              </div>
              {i < pos.etapas.length - 1 && (
                <div style={{
                  flex: 1, height: 3, marginTop: 10,
                  background: pos.etapas[i + 1].ativa ? '#64b5f6' : '#e0e0e0',
                  borderRadius: 2, minWidth: 20,
                }} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div style={{ fontSize: '0.72rem', color: '#1565c0', marginTop: 8, fontWeight: 500 }}>
          Etapa {pos.etapa_atual} de {pos.total_etapas}: {pos.descricao_etapa}
        </div>
      </div>
    );
  };

  // ── Cores de confiança ──
  const corConfBg = (n) => n === 'Alta' ? '#e8f5e9' : n === 'Média' ? '#fff8e1' : '#fff3e0';
  const corConfFg = (n) => n === 'Alta' ? '#2e7d32' : n === 'Média' ? '#f57c00' : '#bf360c';
  const corConfBorder = (n) => n === 'Alta' ? '#a5d6a7' : n === 'Média' ? '#ffcc80' : '#ffab91';

  // Flag: predição de risco alto com confiança insuficiente → investigar
  const investigarAntes = (
    ['Crítico', 'Alto'].includes(ben.nivel_atual) &&
    ['Baixa', 'Média'].includes(ben.nivel_confianca)
  );

  // ── Tab Resumo ──
  const renderResumo = () => (
    <div>
      {/* Banner de investigação quando confiança não sustenta o risco */}
      {investigarAntes && (
        <div style={{
          background: 'linear-gradient(90deg, #fff8e1 0%, #fff3e0 100%)',
          border: '1px solid #ffcc80',
          borderLeft: '4px solid #f57c00',
          borderRadius: 8, padding: '10px 14px', marginBottom: 14,
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <AlertTriangle size={18} style={{ color: '#f57c00', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, fontSize: '0.74rem', color: '#5d4037', lineHeight: 1.5 }}>
            <strong style={{ color: '#e65100' }}>Investigar antes de agir.</strong> O modelo marcou <strong>{ben.nivel_atual}</strong> ({ben.prob_ml}%), mas a confiança nos dados é <strong>{ben.nivel_confianca}</strong> ({ben.confianca_dados}%). Possíveis causas: histórico curto, custo concentrado em um único mês ou uso intermitente. Revisar a timeline abaixo para confirmar se é um caso crônico real ou um evento pontual.
          </div>
        </div>
      )}

      {/* Banner de Dependência Funcional (Camada 2) */}
      {depFunc && depFunc.grave && (
        <div style={{
          background: 'linear-gradient(90deg, #f3e5f5 0%, #ede7f6 100%)',
          border: '1px solid #b39ddb',
          borderLeft: '4px solid #4a148c',
          borderRadius: 8, padding: '12px 14px', marginBottom: 14,
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <ShieldAlert size={18} style={{ color: '#4a148c', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.78rem', color: '#4a148c', fontWeight: 700, marginBottom: 4 }}>
              Padrão de Dependência Funcional Detectado
            </div>
            <div style={{ fontSize: '0.72rem', color: '#5d4037', lineHeight: 1.5, marginBottom: 8 }}>
              {depFunc.motivo && depFunc.motivo.startsWith('âncora')
                ? <>Sinal crítico recorrente identificado: <strong>{depFunc.motivo.replace('âncora: ', '')}</strong>. Isoladamente já caracteriza dependência crônica grave (alto custo estrutural previsível).</>
                : <>Combinação de <strong>{depFunc.n_sinais} sinais</strong> (pontuação {depFunc.score}) característica de reabilitação complexa, pós-AVC grave, neurodegenerativo ou cuidados prolongados.</>
              }
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {depFunc.sinais.map((s, i) => (
                <span key={i} style={{
                  fontSize: '0.66rem', padding: '3px 8px', borderRadius: 12,
                  background: '#fff', color: '#4a148c', fontWeight: 600,
                  border: '1px solid #ce93d8',
                }}>{s}</span>
              ))}
            </div>
            <div style={{ fontSize: '0.62rem', color: '#7b1fa2', marginTop: 6, fontStyle: 'italic' }}>
              {depFunc.eventos_sinal} evento(s) contribuindo para o padrão · detecção baseada em combinação de procedimentos (não em CID)
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {ben.prob_ml != null && (
          <div style={{
            background: '#e8eaf6', borderRadius: 8, padding: '8px 14px',
            borderLeft: '3px solid #283593', minWidth: 120,
          }}>
            <div style={{ fontSize: '0.65rem', color: '#5c6bc0' }}>Prob. Alto Custo</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#283593' }}>{ben.prob_ml}%</div>
          </div>
        )}
        {ben.nivel_confianca && (
          <Tip text={`Confiança nos dados: ${ben.nivel_confianca} (${ben.confianca_dados}%)\n\nCalculada a partir de:\n• Histórico de uso (40%)\n• Dispersão temporal do custo (40%)\n• Recorrência mensal (20%)\n\nAlta → modelo tem base sólida\nMédia/Baixa → investigar antes de agir`}>
            <div style={{
              background: corConfBg(ben.nivel_confianca),
              borderRadius: 8, padding: '8px 14px',
              borderLeft: `3px solid ${corConfFg(ben.nivel_confianca)}`,
              minWidth: 120, cursor: 'help',
            }}>
              <div style={{ fontSize: '0.65rem', color: corConfFg(ben.nivel_confianca) }}>Confiança nos Dados</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: corConfFg(ben.nivel_confianca), display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ letterSpacing: 1 }}>{ben.nivel_confianca === 'Alta' ? '●●●' : ben.nivel_confianca === 'Média' ? '●●○' : '●○○'}</span>
                {ben.nivel_confianca}
              </div>
            </div>
          </Tip>
        )}
        <div style={{
          background: '#fff3e0', borderRadius: 8, padding: '8px 14px',
          borderLeft: '3px solid #e65100', minWidth: 120,
        }}>
          <div style={{ fontSize: '0.65rem', color: '#bf360c' }}>Custo Médio/Mês</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#e65100' }}>{fmt(ben.custo_medio_mensal)}</div>
        </div>
        {resumo.aceleracao_custo_pct != null && (
          <div style={{
            background: resumo.aceleracao_custo_pct > 0 ? '#fbe9e7' : '#e8f5e9',
            borderRadius: 8, padding: '8px 14px',
            borderLeft: `3px solid ${resumo.aceleracao_custo_pct > 0 ? '#b71c1c' : '#2e7d32'}`,
            minWidth: 120,
          }}>
            <div style={{ fontSize: '0.65rem', color: '#546e7a' }}>Aceleração Custo</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: resumo.aceleracao_custo_pct > 0 ? '#b71c1c' : '#2e7d32' }}>
              {resumo.aceleracao_custo_pct > 0 ? '+' : ''}{resumo.aceleracao_custo_pct}%
            </div>
          </div>
        )}
        {ben.cesta_terapeutica && (
          <div style={{
            background: '#f3e5f5', borderRadius: 8, padding: '8px 14px',
            borderLeft: '3px solid #6a1b9a', minWidth: 120,
          }}>
            <div style={{ fontSize: '0.65rem', color: '#7b1fa2' }}>Cesta Terapêutica</div>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#6a1b9a' }}>{ben.cesta_terapeutica}</div>
          </div>
        )}
      </div>

      {/* Stepper */}
      {renderStepper()}

      {/* Motivo */}
      {resumo.motivo_alerta && (
        <div style={{
          background: '#fff8e1', borderRadius: 8, padding: '10px 14px',
          borderLeft: '3px solid #f57f17', fontSize: '0.78rem', color: '#5d4037',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4, color: '#e65100', fontSize: '0.72rem', textTransform: 'uppercase' }}>
            <AlertTriangle size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
            Motivo do Alerta
          </div>
          {resumo.motivo_alerta}
        </div>
      )}

      {/* Similaridade Resumo */}
      {sim && sim.score_similaridade > 0 && (
        <div style={{
          marginTop: 12, background: sim.classificacao === 'alto' ? '#fce4ec' : sim.classificacao === 'moderado' ? '#fff3e0' : '#e8f5e9',
          borderRadius: 8, padding: '10px 14px',
          borderLeft: `3px solid ${sim.classificacao === 'alto' ? '#c62828' : sim.classificacao === 'moderado' ? '#e65100' : '#2e7d32'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
              color: sim.classificacao === 'alto' ? '#c62828' : sim.classificacao === 'moderado' ? '#e65100' : '#2e7d32' }}>
              <Cpu size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
              Similaridade com Padrão de Adoecimento
            </div>
            <span style={{
              fontSize: '1rem', fontWeight: 800,
              color: sim.classificacao === 'alto' ? '#c62828' : sim.classificacao === 'moderado' ? '#e65100' : '#2e7d32',
            }}>{sim.score_similaridade}%</span>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#455a64' }}>{sim.descricao}</div>
          <div style={{ fontSize: '0.65rem', color: '#78909c', marginTop: 4 }}>
            Base: {sim.base_comparacao}
          </div>
        </div>
      )}

      {/* Fatores ML */}
      {top_fatores_ml.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-500)', marginBottom: 6, textTransform: 'uppercase' }}>
            <Cpu size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
            Fatores Preditivos (Modelo Estatístico)
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {top_fatores_ml.map((f, i) => (
              <span key={i} style={{
                fontSize: '0.7rem', padding: '3px 10px', borderRadius: 6,
                background: '#e8eaf6', color: '#283593', fontWeight: 500,
              }}>
                {f.fator}{f.valor ? `: ${f.valor}` : ''}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ── Tab Timeline ──
  const renderTimeline = () => {
    const eventos = timelineExpandida ? timeline_clinica : timeline_clinica.slice(0, 30);
    const totalEventos = timeline_clinica.length;
    const eventosClinicosCount = timeline_clinica.filter(e => e.categoria_clinica).length;

    return (
      <div>
        <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginBottom: 10 }}>
          {totalEventos} eventos · {eventosClinicosCount} com relevância clínica
        </div>
        <div style={{ position: 'relative' }}>
          {eventos.map((ev, i) => {
            const isClinico = ev.relevancia === 'alta';
            const cor = isClinico
              ? (CATEGORIAS_CLINICAS_CORES[ev.categoria_clinica] || '#90a4ae')
              : '#e0e0e0';
            return (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 2 }}>
                {/* Data */}
                <div style={{
                  width: 62, fontSize: '0.62rem', color: 'var(--gray-400)',
                  textAlign: 'right', paddingTop: 2, flexShrink: 0,
                }}>
                  {ev.dt_realizado ? `${ev.dt_realizado.slice(8, 10)}/${ev.dt_realizado.slice(5, 7)}/${ev.dt_realizado.slice(2, 4)}` : ''}
                </div>
                {/* Dot + Line */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16, flexShrink: 0 }}>
                  <div style={{
                    width: isClinico ? 10 : 6, height: isClinico ? 10 : 6,
                    borderRadius: '50%', background: cor,
                    border: isClinico ? `2px solid ${cor}` : 'none',
                    marginTop: 3,
                  }} />
                  {i < eventos.length - 1 && (
                    <div style={{ width: 1, flex: 1, minHeight: 12, background: '#e8e8e8' }} />
                  )}
                </div>
                {/* Content */}
                <div style={{ flex: 1, paddingBottom: 6, minWidth: 0 }}>
                  <div style={{
                    fontSize: '0.72rem',
                    color: isClinico ? 'var(--gray-700)' : 'var(--gray-500)',
                    fontWeight: isClinico ? 600 : 400,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {ev.descricao || '—'}
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {ev.categoria_clinica && (
                      <Pill label={ev.categoria_clinica} color={cor} bg={`${cor}18`} />
                    )}
                    {ev.tipo_evidencia === 'tratamento' && (
                      <Pill label="Tratamento" color="#b71c1c" bg="#ffebee" />
                    )}
                    {ev.tipo_evidencia === 'rastreio' && (
                      <Pill label="Rastreio" color="#1565c0" bg="#e3f2fd" />
                    )}
                    {ev.valor > 0 && (
                      <span style={{ fontSize: '0.62rem', color: 'var(--gray-400)' }}>{fmt(ev.valor)}</span>
                    )}
                    {ev.prestador && (
                      <span style={{ fontSize: '0.58rem', color: 'var(--gray-400)' }}>{ev.prestador}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {totalEventos > 30 && !timelineExpandida && (
          <button onClick={() => setTimelineExpandida(true)} style={{
            background: 'none', border: '1px solid #ddd', borderRadius: 8,
            padding: '6px 16px', cursor: 'pointer', fontSize: '0.72rem',
            color: 'var(--primary, #1565c0)', fontWeight: 600, marginTop: 8,
          }}>
            Ver todos ({totalEventos} eventos)
          </button>
        )}
      </div>
    );
  };

  // ── Tab Categorias ──
  const renderCategorias = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {categorias_emergentes.length === 0 ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontStyle: 'italic' }}>
          Nenhuma categoria emergente detectada neste período
        </div>
      ) : categorias_emergentes.map((cat, i) => (
        <div key={i} style={{
          background: '#fff', borderRadius: 10, padding: '12px 16px',
          border: `1px solid ${cat.cor}30`, borderLeft: `4px solid ${cat.cor}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: cat.cor }}>{cat.categoria}</span>
            <Pill
              label={cat.tipo === 'nova' ? 'NOVA' : 'ACELERANDO'}
              color={cat.tipo === 'nova' ? '#b71c1c' : '#e65100'}
              bg={cat.tipo === 'nova' ? '#ffebee' : '#fff3e0'}
            />
            <span style={{ fontSize: '0.62rem', color: 'var(--gray-400)', marginLeft: 'auto' }}>
              Peso {cat.peso}/5
            </span>
          </div>

          {/* Contadores */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: '0.72rem' }}>
            <span style={{ color: '#b71c1c', fontWeight: 600 }}>
              {cat.tratamentos_recentes} tratamento(s)
            </span>
            {cat.rastreios_recentes > 0 && (
              <span style={{ color: '#1565c0' }}>
                {cat.rastreios_recentes} rastreio(s)
              </span>
            )}
            {cat.tratamentos_anteriores > 0 && (
              <span style={{ color: 'var(--gray-400)' }}>
                Antes: {cat.tratamentos_anteriores}
              </span>
            )}
          </div>

          {/* Eventos-chave */}
          {cat.eventos_chave && cat.eventos_chave.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4, textTransform: 'uppercase' }}>
                Eventos-Chave
              </div>
              {cat.eventos_chave.map((ev, j) => (
                <div key={j} style={{
                  display: 'flex', gap: 8, fontSize: '0.68rem', padding: '2px 0',
                  color: 'var(--gray-600)',
                }}>
                  <span style={{ color: 'var(--gray-400)', width: 55, flexShrink: 0 }}>
                    {ev.dt_realizado ? `${ev.dt_realizado.slice(8, 10)}/${ev.dt_realizado.slice(5, 7)}/${ev.dt_realizado.slice(2, 4)}` : ''}
                  </span>
                  <span style={{ flex: 1, fontWeight: ev.tipo_evidencia === 'tratamento' ? 600 : 400 }}>
                    {ev.descricao}
                  </span>
                  {ev.valor > 0 && <span style={{ color: 'var(--gray-400)' }}>{fmt(ev.valor)}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Referências Alto/Crítico */}
          {cat.referencias && cat.referencias.length > 0 && (
            <div style={{
              background: '#fafafa', borderRadius: 6, padding: '8px 10px', marginTop: 4,
            }}>
              <div style={{ fontSize: '0.62rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4, textTransform: 'uppercase' }}>
                Padrão similar a
              </div>
              {cat.referencias.map((ref, j) => (
                <div key={j} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: '0.68rem', padding: '2px 0', borderBottom: j < cat.referencias.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      background: ref.nivel === 'Crítico' ? '#ffebee' : '#fff3e0',
                      color: ref.nivel === 'Crítico' ? '#b71c1c' : '#e65100',
                      fontSize: '0.58rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                    }}>{ref.nivel}</span>
                    <span style={{ color: 'var(--gray-700)', fontWeight: 500 }}>{ref.nome}</span>
                  </div>
                  <div style={{ color: 'var(--gray-500)' }}>
                    {fmt(ref.custo_medio_mensal)}/mês
                    {ref.prob_ml != null && <span style={{ marginLeft: 6, color: '#283593' }}>{ref.prob_ml}%</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // ── Tab Monitorar ──
  const renderMonitorar = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {monitorar_proximo.length === 0 ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontStyle: 'italic' }}>
          Sem sinais específicos para monitorar no momento
        </div>
      ) : monitorar_proximo.map((s, i) => {
        const corPrio = s.prioridade === 'alta' ? '#b71c1c' : s.prioridade === 'media' ? '#e65100' : '#546e7a';
        const bgPrio = s.prioridade === 'alta' ? '#ffebee' : s.prioridade === 'media' ? '#fff3e0' : '#f5f5f5';
        return (
          <div key={i} style={{
            display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 8,
            background: bgPrio, borderLeft: `3px solid ${corPrio}`,
          }}>
            <AlertTriangle size={14} color={corPrio} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-700)' }}>
                  {s.sinal}
                </span>
                <Pill label={s.prioridade} color={corPrio} bg={`${corPrio}18`} />
              </div>
              <div style={{ fontSize: '0.68rem', color: 'var(--gray-500)' }}>
                {s.justificativa}
              </div>
              <div style={{ fontSize: '0.62rem', color: 'var(--gray-400)', marginTop: 2 }}>
                Categoria: {s.categoria}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ── Tab Similaridade ──
  const renderSimilaridade = () => {
    if (!sim) return (
      <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontStyle: 'italic' }}>
        Análise de similaridade não disponível para este beneficiário
      </div>
    );

    const corScore = sim.classificacao === 'alto' ? '#c62828' : sim.classificacao === 'moderado' ? '#e65100' : sim.classificacao === 'baixo' ? '#2e7d32' : '#78909c';
    const bgScore = sim.classificacao === 'alto' ? '#fce4ec' : sim.classificacao === 'moderado' ? '#fff3e0' : sim.classificacao === 'baixo' ? '#e8f5e9' : '#f5f5f5';
    const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
    const det = sim.detalhes || {};

    return (
      <div>
        {/* Score Principal */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, padding: '14px 18px', borderRadius: 10, background: bgScore, borderLeft: `4px solid ${corScore}` }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: corScore, lineHeight: 1 }}>{sim.score_similaridade}%</div>
            <div style={{ fontSize: '0.65rem', color: corScore, fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>similaridade</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--gray-700)', marginBottom: 4 }}>{sim.descricao}</div>
            <div style={{ fontSize: '0.68rem', color: 'var(--gray-500)' }}>
              {sim.total_eventos} eventos · {sim.especialidades_distintas} especialidades · {sim.base_comparacao}
            </div>
          </div>
        </div>

        {/* KPIs rápidos */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 90, background: det.custo_farmacia > 2000 ? '#fff3e0' : '#fafafa', borderRadius: 8, padding: '8px 12px', border: `1px solid ${det.custo_farmacia > 2000 ? '#e65100' : '#eee'}` }}>
            <div style={{ fontSize: '0.6rem', color: '#999', textTransform: 'uppercase' }}>Farmácia</div>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: det.custo_farmacia > 2000 ? '#e65100' : '#455a64' }}>{fmt(det.custo_farmacia)}</div>
          </div>
          <div style={{ flex: 1, minWidth: 90, background: '#fafafa', borderRadius: 8, padding: '8px 12px', border: '1px solid #eee' }}>
            <div style={{ fontSize: '0.6rem', color: '#999', textTransform: 'uppercase' }}>Custo Total</div>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#455a64' }}>{fmt(det.custo_total)}</div>
          </div>
          <div style={{ flex: 1, minWidth: 90, background: det.procedimentos_complexos > 3 ? '#fce4ec' : '#fafafa', borderRadius: 8, padding: '8px 12px', border: `1px solid ${det.procedimentos_complexos > 3 ? '#c62828' : '#eee'}` }}>
            <div style={{ fontSize: '0.6rem', color: '#999', textTransform: 'uppercase' }}>Proc. Complexos</div>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: det.procedimentos_complexos > 3 ? '#c62828' : '#455a64' }}>{det.procedimentos_complexos || 0}</div>
          </div>
        </div>

        {/* 6 Componentes do Score */}
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-500)', marginBottom: 8, textTransform: 'uppercase' }}>
          Composição do Score (6 componentes)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
          {[
            { label: 'Farmácia', valor: sim.componentes.farmacia, peso: '20%', cor: '#e65100', desc: 'Gasto com medicamentos' },
            { label: 'Especialidades', valor: sim.componentes.especialidades, peso: '20%', cor: '#1565c0', desc: 'Especialidades discriminantes' },
            { label: 'Custo/Grupo', valor: sim.componentes.custo_por_grupo, peso: '15%', cor: '#283593', desc: 'Custo por especialidade' },
            { label: 'Procedimentos', valor: sim.componentes.procedimentos, peso: '15%', cor: '#6a1b9a', desc: 'Procedimentos discriminantes' },
            { label: 'Complexidade', valor: sim.componentes.complexidade, peso: '15%', cor: '#c62828', desc: 'Biópsias, RM, endoscopia...' },
            { label: 'Volume', valor: sim.componentes.volume, peso: '15%', cor: '#00695c', desc: 'Total de eventos e custo' },
          ].map((c, i) => (
            <div key={i} style={{ background: '#fafafa', borderRadius: 8, padding: '8px 10px', border: '1px solid #eee' }}>
              <div style={{ fontSize: '0.58rem', color: '#999', marginBottom: 1 }}>{c.label} ({c.peso})</div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: c.cor }}>{(c.valor || 0).toFixed(0)}%</div>
              <div style={{ height: 4, borderRadius: 2, background: '#e0e0e0', marginTop: 3 }}>
                <div style={{ height: '100%', borderRadius: 2, background: c.cor, width: `${Math.min(c.valor || 0, 100)}%`, transition: 'width 0.3s' }} />
              </div>
              <div style={{ fontSize: '0.55rem', color: '#bbb', marginTop: 2 }}>{c.desc}</div>
            </div>
          ))}
        </div>

        {/* Procedimentos complexos encontrados */}
        {det.procs_complexos_lista && det.procs_complexos_lista.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-500)', marginBottom: 8, textTransform: 'uppercase' }}>
              Procedimentos Complexos Identificados
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {det.procs_complexos_lista.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, background: '#fce4ec', borderLeft: '3px solid #c62828' }}>
                  <AlertTriangle size={12} color="#c62828" />
                  <span style={{ flex: 1, fontSize: '0.72rem', color: '#455a64' }}>{(p.procedimento || '').substring(0, 65)}</span>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#c62828' }}>{p.vezes}x</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Matches por grupo de especialidade */}
        {sim.matches_grupo && sim.matches_grupo.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-500)', marginBottom: 8, textTransform: 'uppercase' }}>
              Especialidades em Comum com Transicionados
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sim.matches_grupo.map((m, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  borderRadius: 8, background: m.relevancia === 'alta' ? '#fff3e0' : '#f5f5f5',
                  borderLeft: `3px solid ${m.relevancia === 'alta' ? '#e65100' : '#90a4ae'}`,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-700)' }}>{m.grupo}</span>
                      <Pill label={m.relevancia} color={m.relevancia === 'alta' ? '#e65100' : '#546e7a'} bg={m.relevancia === 'alta' ? '#e6510018' : '#546e7a18'} />
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#78909c', marginTop: 2 }}>
                      {m.eventos_beneficiario} eventos ({fmt(m.custo_beneficiario || 0)}) · Transic. média: {m.media_transicionados} · Saudáveis: {m.media_controle}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#e65100' }}>
                    +{m.delta_pct}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Matches por procedimento */}
        {sim.matches_procedimento && sim.matches_procedimento.length > 0 && (
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-500)', marginBottom: 8, textTransform: 'uppercase' }}>
              Procedimentos Discriminantes em Comum
            </div>
            <table style={{ width: '100%', fontSize: '0.72rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#78909c' }}>Procedimento</th>
                  <th style={{ textAlign: 'center', padding: '4px 8px', color: '#78909c' }}>Vezes</th>
                  <th style={{ textAlign: 'center', padding: '4px 8px', color: '#78909c' }}>Delta</th>
                </tr>
              </thead>
              <tbody>
                {sim.matches_procedimento.map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: p.complexo ? '#fce4ec22' : 'transparent' }}>
                    <td style={{ padding: '4px 8px', color: 'var(--gray-700)' }}>
                      {p.procedimento.substring(0, 55)}{p.procedimento.length > 55 ? '...' : ''}
                      {p.complexo && <span style={{ fontSize: '0.55rem', color: '#c62828', marginLeft: 4, fontWeight: 700 }}>COMPLEXO</span>}
                    </td>
                    <td style={{ textAlign: 'center', padding: '4px 8px', fontWeight: 700, color: '#1565c0' }}>{p.vezes_beneficiario}x</td>
                    <td style={{ textAlign: 'center', padding: '4px 8px', fontWeight: 700, color: '#e65100' }}>+{p.delta_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '12px 0' }}>
      {renderAbas()}
      {abaAtiva === 'resumo' && renderResumo()}
      {abaAtiva === 'similaridade' && renderSimilaridade()}
      {abaAtiva === 'timeline' && renderTimeline()}
      {abaAtiva === 'categorias' && renderCategorias()}
      {abaAtiva === 'monitorar' && renderMonitorar()}
    </div>
  );
}

// ── ScoreRow (expandível) ────────────────────────────────────────────────────
function ScoreRow({ s, SCORE_COR }) {
  const [expanded, setExpanded] = useState(false);
  const cesta = LABEL_CESTA[s.cesta_terapeutica] || {};
  const sinais = Array.isArray(s.sinais_principais) ? s.sinais_principais : [];

  return (
    <>
      <tr style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
        onClick={() => setExpanded(v => !v)}
        onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
        onMouseLeave={e => e.currentTarget.style.background = ''}>
        <td style={{ padding: '10px 12px', fontWeight: 600, fontSize: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {expanded ? <ChevronUp size={14} color="var(--gray-400)" /> : <ChevronDown size={14} color="var(--gray-400)" />}
            <div>
              <div>{s.nome || s.matricula_soul}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', fontWeight: 400 }}>
                Contrato {s.contrato} · {s.plano}
              </div>
            </div>
          </div>
        </td>
        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
          {s.score != null ? (
            <div style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 36, height: 36, borderRadius: '50%',
              background: `${SCORE_COR(s.score)}20`, color: SCORE_COR(s.score),
              fontWeight: 800, fontSize: '1rem',
            }}>{s.score}</div>
          ) : (
            <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)', fontStyle: 'italic' }}>—</span>
          )}
        </td>
        <td style={{ padding: '10px 12px', fontSize: '0.82rem', color: s.score != null ? 'var(--gray-700)' : 'var(--gray-400)' }}>
          {s.classificacao}
          {s.custo_total > 0 && s.score == null && (
            <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)' }}>
              R$ {s.custo_total.toLocaleString('pt-BR', { minimumFractionDigits: 0 })} · {s.total_eventos} eventos
            </div>
          )}
        </td>
        <td style={{ padding: '10px 12px' }}>
          {s.cesta_terapeutica && (
            <Pill label={s.cesta_terapeutica} color={cesta.color || '#546e7a'} bg={cesta.bg || '#eceff1'} tip={cesta.tip} />
          )}
        </td>
        <td style={{ padding: '10px 12px', fontSize: '0.78rem', color: 'var(--gray-600)' }}>
          {sinais.slice(0, 2).map((sig, j) => <div key={j} style={{ marginBottom: 1 }}>· {sig}</div>)}
          {sinais.length > 2 && <div style={{ color: 'var(--gray-400)' }}>+{sinais.length - 2} mais</div>}
        </td>
        <td style={{ padding: '10px 12px', fontSize: '0.78rem', color: '#1565c0' }}>{s.recomendacao}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} style={{ padding: '0 12px 12px 24px', background: '#fafafa' }}>
            <DetalheExpand matricula={s.matricula_soul} />
          </td>
        </tr>
      )}
    </>
  );
}

// ── AbaSinistralidade ─────────────────────────────────────────────────────────
function AbaSinistralidade({ statusJob, busca }) {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [scoreMin, setScoreMin] = useState(0);
  const [scoreMax, setScoreMax] = useState(10);

  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const params = { score_min: scoreMin, score_max: scoreMax, limit: 200 };
      if (busca) params.busca = busca;
      const r = await api.get('/radar-carteira/scores', { params });
      setDados(r.data);
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao carregar scores');
    } finally { setLoading(false); }
  }, [scoreMin, scoreMax, busca]);

  useEffect(() => { if (statusJob?.scores_disponiveis) carregar(); }, [carregar, statusJob]);

  const scores = dados?.scores || [];
  const SCORE_COR = (s) => {
    if (s >= 8) return '#b71c1c';
    if (s >= 6) return '#e65100';
    if (s >= 4) return '#f57f17';
    return '#2e7d32';
  };

  if (!statusJob?.scores_disponiveis) {
    return (
      <div style={{
        background: '#fff', borderRadius: 12, padding: '3rem',
        textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        <Clock size={32} color="#9e9e9e" style={{ marginBottom: 12 }} />
        <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--gray-700)', marginBottom: 8 }}>
          Aguardando primeiro processamento
        </div>
        <div style={{ fontSize: '0.85rem', color: 'var(--gray-400)', maxWidth: 420, margin: '0 auto' }}>
          O job roda sozinho todo dia às 07h00 e às 12h00, depois do snapshot de inadimplência. Administradores podem disparar na hora pelo botão "Executar Job".
        </div>
        <div style={{ marginTop: 12, fontSize: '0.75rem', color: 'var(--gray-400)' }}>
          Fonte dos dados: integração de utilização e cadastro do ERP da operadora · Modelo: IA
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Filtros de score */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: '1rem 1.2rem',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '1rem',
        display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center',
      }}>
        <Tip text="Score 1-10 calculado por GPT-4o\n1=saudável/preventivo | 10=alto risco iminente\nFonte: esau_v_fonte_item02, 12 meses de utilização">
          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Filter size={14} /> Score de Risco
          </span>
        </Tip>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>De</span>
          <select value={scoreMin} onChange={e => setScoreMin(Number(e.target.value))}
            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e0e0e0', fontSize: '0.78rem' }}>
            {[0,1,2,3,4,5,6,7,8,9,10].map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>até</span>
          <select value={scoreMax} onChange={e => setScoreMax(Number(e.target.value))}
            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e0e0e0', fontSize: '0.78rem' }}>
            {[0,1,2,3,4,5,6,7,8,9,10].map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <button onClick={carregar} disabled={loading}
            style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: 'var(--primary)', color: '#fff', fontSize: '0.78rem' }}>
            Filtrar
          </button>
        </div>
      </div>

      {erro && <ErroBanner msg={erro} />}
      {loading && <LoadingBanner msg="Carregando scores de sinistralidade…" sub="Fonte: crm.radar_scores, calculado por GPT-4o em esau_v_fonte_item02" />}

      {!loading && scores.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                <Th label="Beneficiário" tip="Nome e contrato (crm.beneficiarios)" />
                <Th label="Score IA" center tip="Score 1-10 calculado por GPT-4o\n1=preventivo | 10=alto risco\nFonte: crm.radar_scores.score" />
                <Th label="Classificação" tip="Rótulo dado pelo GPT-4o\nExs: 'Alto risco crônico', 'Bomba-relógio silenciosa'\nFonte: crm.radar_scores.classificacao" />
                <Th label="Cesta Terapêutica" tip="Grupo de uso: Preventivo / Crônico Estável / Crônico Complexo / Alto Custo / Uso Agudo Isolado\nFonte: crm.radar_scores.cesta_terapeutica" />
                <Th label="Sinais" tip="Até 3 fatores que justificam o score, gerados por GPT-4o\nFonte: crm.radar_scores.sinais_principais (JSONB)" />
                <Th label="Recomendação" tip="Ação sugerida pelo GPT-4o\nFonte: crm.radar_scores.recomendacao" />
              </tr>
            </thead>
            <tbody>
              {scores.map((s, i) => (
                <ScoreRow key={i} s={s} SCORE_COR={SCORE_COR} />
              ))}
            </tbody>
          </table>
          <TableFooter count={scores.length}
            note="Fonte: crm.radar_scores, calculado por GPT-4o sobre esau_v_fonte_item02 (FDW Oracle)" />
        </div>
      )}
      {!loading && dados && scores.length === 0 && <VazioMsg />}
    </div>
  );
}

// ── AbaAuditoria ──────────────────────────────────────────────────────────────
function AbaAuditoria({ statusJob, busca }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [filtNivel, setFiltNivel] = useState('');
  const [filtModulo, setFiltModulo] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const params = { limit: 200 };
      if (filtNivel) params.nivel = filtNivel;
      if (filtModulo) params.modulo = filtModulo;
      if (busca) params.busca = busca;
      const r = await api.get('/radar-carteira/anomalias', { params });
      setDados(r.data);
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao carregar anomalias');
    } finally { setLoading(false); }
  }, [filtNivel, filtModulo, busca]);

  useEffect(() => { if (statusJob?.scores_disponiveis) carregar(); }, [carregar, statusJob]);

  const anomalias = dados?.anomalias || [];

  const resolver = async (id) => {
    await api.put(`/radar-carteira/anomalias/${id}/resolver`);
    carregar();
  };

  if (!statusJob?.scores_disponiveis) {
    return (
      <div style={{
        background: '#fff', borderRadius: 12, padding: '3rem',
        textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        <Clock size={32} color="#9e9e9e" style={{ marginBottom: 12 }} />
        <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--gray-700)', marginBottom: 8 }}>
          Aguardando primeiro processamento
        </div>
        <div style={{ fontSize: '0.85rem', color: 'var(--gray-400)' }}>
          Anomalias são detectadas durante o job IA noturno (07h00 e 12h00).
        </div>
      </div>
    );
  }

  const btnFiltro = (label, val, ativo, onClick) => (
    <button key={val} onClick={onClick} style={{
      padding: '5px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
      background: ativo ? 'var(--primary)' : '#f0f0f0',
      color: ativo ? '#fff' : 'var(--gray-600)',
      fontWeight: 600, fontSize: '0.78rem',
    }}>{label}</button>
  );

  return (
    <div>
      <div style={{
        background: '#fff', borderRadius: 12, padding: '1rem 1.2rem',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '1rem',
        display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Filter size={14} color="var(--gray-400)" />
          <Tip text="Nível de suspeita determinado por GPT-4o\nFonte: crm.radar_anomalias.nivel">
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)' }}>Nível</span>
          </Tip>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['Todos',''],['Alto','Alto'],['Médio','Médio'],['Baixo','Baixo']].map(([lbl,val]) =>
            btnFiltro(lbl, val, filtNivel === val, () => setFiltNivel(val))
          )}
        </div>
        <div style={{ width: 1, height: 20, background: '#e0e0e0' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Tip text="Módulo de detecção (crm.radar_anomalias.modulo)\n2A=Prestador | 2B=Beneficiário | 2C=Cadastro | 2D=Conluio">
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)' }}>Módulo</span>
          </Tip>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['Todos',''],['2A · Prestador','2A'],['2B · Beneficiário','2B'],['2C · Cadastro','2C'],['2D · Conluio','2D']].map(([lbl,val]) =>
            btnFiltro(lbl, val, filtModulo === val, () => setFiltModulo(val))
          )}
        </div>
      </div>

      {erro && <ErroBanner msg={erro} />}
      {loading && <LoadingBanner msg="Carregando anomalias…" sub="Fonte: crm.radar_anomalias, detectadas por GPT-4o" />}

      {!loading && anomalias.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {anomalias.map((a) => (
            <AnomaliaCard key={a.id} a={a} resolver={resolver} />
          ))}
        </div>
      )}
      {!loading && dados && anomalias.length === 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: '3rem', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <CheckCircle2 size={32} color="#a5d6a7" style={{ marginBottom: 12 }} />
          <div style={{ fontSize: '0.9rem', color: 'var(--gray-500)' }}>Nenhuma anomalia encontrada para os filtros selecionados.</div>
        </div>
      )}
    </div>
  );
}

function AnomaliaCard({ a, resolver }) {
  const [expandido, setExpandido] = useState(false);
  const nc = CORES_NIVEL[a.nivel] || CORES_NIVEL.Baixo;
  const mod = LABEL_MODULO[a.modulo] || { label: a.modulo, tip: '' };
  const evidencias = Array.isArray(a.evidencias) ? a.evidencias : [];
  return (
              <div style={{
                background: '#fff', borderRadius: 12, padding: '1rem 1.2rem',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                borderLeft: `4px solid ${nc.border}`,
                cursor: 'pointer',
              }}
              onClick={() => setExpandido(!expandido)}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <Tip text={`Nível de suspeita determinado por GPT-4o\nFonte: crm.radar_anomalias.nivel`}>
                      <span style={{
                        background: nc.bg, color: nc.color,
                        fontWeight: 700, fontSize: '0.72rem', padding: '3px 10px', borderRadius: 8,
                      }}>{a.nivel}</span>
                    </Tip>
                    <Tip text={mod.tip}>
                      <span style={{
                        background: '#f0f0f0', color: '#424242',
                        fontWeight: 600, fontSize: '0.72rem', padding: '3px 10px', borderRadius: 8,
                      }}>{mod.label}</span>
                    </Tip>
                    {a.nome_beneficiario && (
                      <Tip text={`Beneficiário envolvido (crm.beneficiarios)\nMatrícula: ${a.matricula_soul}`}>
                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--gray-700)' }}>
                          {a.nome_beneficiario}
                        </span>
                      </Tip>
                    )}
                    {!a.nome_beneficiario && a.matricula_soul && (
                      <Tip text={`Matrícula do beneficiário envolvido\nFonte: crm.radar_anomalias.matricula_soul`}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--gray-600)', fontWeight: 500 }}>
                          Mat. {a.matricula_soul}
                        </span>
                      </Tip>
                    )}
                    {a.cd_prestador && (
                      <Tip text={`Código do prestador envolvido na anomalia\nFonte: esau_v_fonte_item02.cd_prestador`}>
                        <span style={{
                          fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px', borderRadius: 8,
                          background: '#e3f2fd', color: '#1565c0',
                        }}>
                          <Building2 size={11} style={{ verticalAlign: '-2px', marginRight: 3 }} />
                          Prestador {a.cd_prestador}
                        </span>
                      </Tip>
                    )}
                  </div>
                  <Tip text="Marcar como resolvida (salvo em crm.radar_anomalias.resolvida)">
                    <button onClick={() => resolver(a.id)} style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '4px 10px', borderRadius: 6, border: '1px solid #e0e0e0',
                      cursor: 'pointer', background: '#f5f5f5', color: '#424242',
                      fontSize: '0.72rem', fontWeight: 600,
                    }}>
                      <CheckCircle2 size={12} /> Resolver
                    </button>
                  </Tip>
                </div>

                <p style={{ fontSize: '0.85rem', color: 'var(--gray-700)', margin: '8px 0 6px' }}>
                  <Tip text="Descrição da anomalia gerada por GPT-4o\nFonte: crm.radar_anomalias.descricao">
                    <span>{a.descricao}</span>
                  </Tip>
                </p>

                {evidencias.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                    {evidencias.map((ev, j) => (
                      <Tip key={j} text="Evidência listada por GPT-4o\nFonte: crm.radar_anomalias.evidencias (JSONB)">
                        <span style={{
                          background: '#f8f9fa', border: '1px solid #e0e0e0',
                          borderRadius: 6, padding: '3px 8px', fontSize: '0.72rem', color: 'var(--gray-600)',
                        }}>· {ev}</span>
                      </Tip>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Zap size={12} color="#1565c0" />
                  <Tip text="Ação recomendada pelo GPT-4o\nFonte: crm.radar_anomalias.acao">
                    <span style={{ fontSize: '0.78rem', color: '#1565c0', fontWeight: 600 }}>{a.acao}</span>
                  </Tip>
                  <Tip text="Data em que a anomalia foi detectada pelo job\nFonte: crm.radar_anomalias.data_ref">
                    <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginLeft: 'auto' }}>
                      Detectada em {a.data_ref}
                    </span>
                  </Tip>
                </div>

                {expandido && a.matricula_soul && (
                  <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 10 }}>
                    <DetalheExpand matricula={a.matricula_soul} />
                  </div>
                )}
              </div>
  );
}

// ── AbaCurvaDoenca (Early Warning) ───────────────────────────────────────────
const CORES_NIVEL_CURVA = {
  'Crítico':  { bg: '#ffebee', color: '#b71c1c', border: '#ef5350' },
  'Alto':     { bg: '#fff3e0', color: '#e65100', border: '#ff9800' },
  'Moderado': { bg: '#fff8e1', color: '#f57f17', border: '#ffc107' },
  'Atenção':  { bg: '#e8f5e9', color: '#2e7d32', border: '#66bb6a' },
  'Saudável': { bg: '#eceff1', color: '#546e7a', border: '#90a4ae' },
};

function MiniSparkline({ dados, cor = '#1565c0' }) {
  if (!dados || dados.length < 2) return null;
  const max = Math.max(...dados.map(d => d.custo));
  const w = 160, h = 36, pad = 2;
  const pts = dados.map((d, i) => {
    const x = pad + (i / (dados.length - 1)) * (w - pad * 2);
    const y = h - pad - ((d.custo / (max || 1)) * (h - pad * 2));
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={cor} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      {dados.map((d, i) => {
        const x = pad + (i / (dados.length - 1)) * (w - pad * 2);
        const y = h - pad - ((d.custo / (max || 1)) * (h - pad * 2));
        return <circle key={i} cx={x} cy={y} r={2.5} fill={cor} />;
      })}
    </svg>
  );
}

function AlertaRow({ alerta }) {
  const [expandido, setExpandido] = useState(false);
  const nc = CORES_NIVEL_CURVA[alerta.nivel] || CORES_NIVEL_CURVA['Saudável'] || { bg: '#e8f5e9', color: '#2e7d32', border: '#66bb6a' };
  const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
  const categorias = alerta.categorias || [];
  return (
    <div
      onClick={() => setExpandido(!expandido)}
      style={{
        background: '#fff', borderRadius: 12, padding: '1rem 1.2rem',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer',
        borderLeft: `4px solid ${nc.border}`, transition: 'box-shadow 0.15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            background: nc.bg, color: nc.color,
            fontWeight: 700, fontSize: '0.72rem', padding: '3px 10px', borderRadius: 8,
          }}>{alerta.nivel}</span>
          {alerta.prob_ml != null ? (
            <Tip text={`Prob. alto custo: ${alerta.prob_ml}%\nModelo preditivo treinado em dados históricos\n${alerta.top_fatores_ml || ''}`}>
              <span style={{
                background: '#e8eaf6', color: '#283593',
                fontWeight: 700, fontSize: '0.72rem', padding: '3px 10px', borderRadius: 8,
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}><Cpu size={10} /> {alerta.prob_ml}%</span>
            </Tip>
          ) : (
            <span style={{
              background: '#f5f5f5', color: '#78909c',
              fontWeight: 700, fontSize: '0.72rem', padding: '3px 10px', borderRadius: 8,
            }}>Score {alerta.score_trajetoria}</span>
          )}
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)' }}>
            {alerta.nome || alerta.matricula_soul}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <MiniSparkline dados={alerta.trajetoria} cor={nc.border} />
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--gray-700)' }}>
              {fmt(alerta.custo_medio_mensal)}/mês
            </div>
            <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)' }}>
              {alerta.meses_uso} meses · {alerta.total_eventos} eventos
            </div>
          </div>
          {expandido ? <ChevronUp size={16} color="var(--gray-400)" /> : <ChevronDown size={16} color="var(--gray-400)" />}
        </div>
      </div>

      {/* Categorias clínicas (pills) */}
      {categorias.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {categorias.map((cat, i) => (
            <Tip key={i} text={`${cat.categoria}: ${cat.tratamento || 0} tratamento(s), ${cat.rastreio || 0} rastreio(s) · peso ${cat.peso}/5\n${cat.tipo}`}>
              <span style={{
                fontSize: '0.68rem', fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                background: `${cat.cor}15`, color: cat.cor, border: `1px solid ${cat.cor}30`,
                display: 'inline-flex', alignItems: 'center', gap: 3,
              }}>
                {cat.tratamento > 0 && <span style={{ width: 6, height: 6, borderRadius: '50%', background: cat.cor, display: 'inline-block' }} />}
                {cat.categoria} ({cat.evidencias})
              </span>
            </Tip>
          ))}
          {alerta.cesta_terapeutica && (
            <span style={{
              fontSize: '0.68rem', fontWeight: 600, padding: '2px 8px', borderRadius: 6,
              background: '#f3e5f5', color: '#6a1b9a',
            }}>{alerta.cesta_terapeutica}</span>
          )}
        </div>
      )}

      {expandido && (
        <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ flex: '1 1 280px' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-500)', marginBottom: 6, textTransform: 'uppercase' }}>
                Indicadores de Alerta
              </div>
              {(alerta.indicadores || []).map((ind, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: '0.78rem', color: 'var(--gray-600)', marginBottom: 4,
                }}>
                  <AlertTriangle size={12} color={nc.color} /> {ind}
                </div>
              ))}
              {categorias.length === 0 && (
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontStyle: 'italic' }}>
                  Nenhuma categoria clínica de risco identificada
                </div>
              )}
            </div>
            <div style={{ flex: '1 1 280px' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-500)', marginBottom: 6, textTransform: 'uppercase' }}>
                Trajetória de Custo Mensal
              </div>
              <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {(alerta.trajetoria || []).slice(-18).map((t, i) => {
                  const traj = (alerta.trajetoria || []).slice(-18);
                  const max = Math.max(...traj.map(x => x.custo));
                  const pct = max > 0 ? (t.custo / max) * 100 : 0;
                  return (
                    <Tip key={i} text={`${t.mes}: ${fmt(t.custo)}`}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <div style={{
                          width: 16, height: 44, background: '#f5f5f5', borderRadius: 3,
                          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                        }}>
                          <div style={{
                            width: '100%', borderRadius: '3px 3px 0 0',
                            height: `${Math.max(pct, 3)}%`,
                            background: nc.border,
                          }} />
                        </div>
                        <span style={{ fontSize: '0.5rem', color: 'var(--gray-400)' }}>
                          {t.mes.slice(5)}
                        </span>
                      </div>
                    </Tip>
                  );
                })}
              </div>
            </div>
          </div>
          {alerta.empresa && (
            <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginBottom: 8 }}>
              <Building2 size={11} style={{ verticalAlign: '-2px', marginRight: 4 }} />
              {alerta.empresa} · Mat. {alerta.matricula_soul}
              {alerta.score_ia != null && ` · Score IA: ${alerta.score_ia}`}
              {alerta.prob_ml != null && (
                <span style={{ marginLeft: 8, color: '#283593', fontWeight: 600 }}>
                  <Cpu size={10} style={{ verticalAlign: '-1px' }} /> {alerta.prob_ml}% prob. alto custo
                </span>
              )}
            </div>
          )}
          {alerta.top_fatores_ml && (
            <div style={{ fontSize: '0.72rem', color: '#546e7a', background: '#e8eaf6', borderRadius: 6, padding: '4px 10px', marginBottom: 8 }}>
              <Cpu size={10} style={{ verticalAlign: '-1px', marginRight: 4 }} />
              <strong>Fatores preditivos (modelo):</strong> {alerta.top_fatores_ml}
            </div>
          )}
          <PainelInvestigacao matricula={alerta.matricula_soul} />
        </div>
      )}
    </div>
  );
}

function AbaCurvaDoenca({ busca }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  // Filtros clínicos para linha de cuidado
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroNivel, setFiltroNivel] = useState('');
  const [filtroEvidencia, setFiltroEvidencia] = useState('');
  const [filtroMulti, setFiltroMulti] = useState('');
  const [filtroCusto, setFiltroCusto] = useState('');
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const CATEGORIAS = [
    'Diabetes', 'Cardiovascular', 'Autoimune/Inflamatório', 'Renal',
    'Tireoide', 'Oncológico', 'Saúde Mental', 'Dislipidemia',
    'Internação/Urgência', 'TEA/Neurodesenvolvimento', 'Fonoaudiologia',
    'Fisioterapia/Reabilitação', 'Ortopédico/Coluna',
    'Materno-Infantil', 'Obesidade/Bariátrica',
  ];
  const NIVEIS = ['Crítico', 'Alto', 'Moderado', 'Atenção', 'Saudável'];
  const EVIDENCIAS = [
    { value: '', label: 'Todos' },
    { value: 'tratamento', label: 'Tratamento ativo' },
    { value: 'rastreio', label: 'Apenas rastreio' },
  ];
  const MULTIMORBIDADE = [
    { value: '', label: 'Qualquer' },
    { value: '2', label: '2+ áreas' },
    { value: '3', label: '3+ áreas' },
    { value: '4', label: '4+ áreas' },
  ];
  const FAIXAS_CUSTO = [
    { value: '', label: 'Qualquer' },
    { value: '0-500', label: 'Até R$ 500/mês' },
    { value: '500-2000', label: 'R$ 500 – 2.000/mês' },
    { value: '2000-5000', label: 'R$ 2.000 – 5.000/mês' },
    { value: '5000-10000', label: 'R$ 5.000 – 10.000/mês' },
    { value: '10000+', label: 'Acima R$ 10.000/mês' },
  ];

  const filtrosAtivos = [filtroCategoria, filtroNivel, filtroEvidencia, filtroMulti, filtroCusto].filter(Boolean).length;

  const carregar = useCallback(() => {
    setLoading(true); setErro(null);
    const params = { limit: 100 };
    if (busca) params.busca = busca;
    if (filtroCategoria) params.categoria = filtroCategoria;
    if (filtroNivel && !busca) params.nivel = filtroNivel;
    if (filtroEvidencia) params.tipo_evidencia = filtroEvidencia;
    if (filtroMulti) params.multimorbidade = filtroMulti;
    if (filtroCusto) params.faixa_custo = filtroCusto;
    api.get('/radar-carteira/curva-doenca', { params })
      .then(r => setDados(r.data))
      .catch(e => setErro(e.response?.data?.detail || 'Erro ao carregar curva de doença'))
      .finally(() => setLoading(false));
  }, [busca, filtroCategoria, filtroNivel, filtroEvidencia, filtroMulti, filtroCusto]);

  useEffect(() => { carregar(); }, [carregar]);

  const alertas = dados?.alertas || [];
  const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;

  const limparFiltros = () => {
    setFiltroCategoria(''); setFiltroNivel(''); setFiltroEvidencia('');
    setFiltroMulti(''); setFiltroCusto('');
  };

  const chipStyle = (ativo) => ({
    fontSize: '0.72rem', fontWeight: 600, padding: '4px 12px', borderRadius: 20,
    cursor: 'pointer', transition: 'all 0.15s', border: '1.5px solid',
    background: ativo ? '#1565c0' : '#fff',
    color: ativo ? '#fff' : '#546e7a',
    borderColor: ativo ? '#1565c0' : '#cfd8dc',
  });

  const selectStyle = {
    fontSize: '0.78rem', padding: '6px 10px', borderRadius: 8,
    border: '1.5px solid #cfd8dc', background: '#fff', color: '#37474f',
    outline: 'none', cursor: 'pointer', minWidth: 140,
  };

  return (
    <div>
      <div style={{
        background: 'linear-gradient(135deg, #fff3e0 0%, #ffecb3 100%)',
        borderRadius: 12, padding: '1.2rem 1.4rem', marginBottom: '1rem',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <TrendingUp size={18} color="#e65100" />
          <span style={{ fontSize: '0.92rem', fontWeight: 700, color: '#e65100' }}>
            Early Warning: Curva de Doença
          </span>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#795548', margin: 0, lineHeight: 1.5 }}>
          Estratificação de risco da carteira de <strong>{dados?.referencia?.total_ativos?.toLocaleString('pt-BR') || '...'} beneficiários ativos</strong> baseada
          no modelo APS (Pirâmide de Kaiser). Classifica por <strong>linha de cuidado</strong> (diabetes, cardiovascular,
          oncológico...), <strong>tipo de evidência</strong> (tratamento ativo vs rastreio), <strong>multimorbidade</strong> e
          <strong>custo</strong>. Beneficiários sem histórico entram como <em>Atenção (risco desconhecido)</em>.
        </p>
      </div>

      {/* ── Filtros Clínicos (Linha de Cuidado) ── */}
      <div style={{
        background: '#fff', borderRadius: 12, marginBottom: '1rem',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden',
      }}>
        <div
          onClick={() => setMostrarFiltros(!mostrarFiltros)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0.8rem 1.2rem', cursor: 'pointer',
            borderBottom: mostrarFiltros ? '1px solid #f0f0f0' : 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Filter size={15} color="#546e7a" />
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#37474f' }}>
              Filtros Clínicos: Linha de Cuidado
            </span>
            {filtrosAtivos > 0 && (
              <span style={{
                fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                background: '#1565c0', color: '#fff',
              }}>{filtrosAtivos} ativo{filtrosAtivos > 1 ? 's' : ''}</span>
            )}
          </div>
          {mostrarFiltros ? <ChevronUp size={16} color="#90a4ae" /> : <ChevronDown size={16} color="#90a4ae" />}
        </div>

        {mostrarFiltros && (
          <div style={{ padding: '1rem 1.2rem', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Linha de Cuidado (categoria) */}
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#78909c', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Linha de Cuidado
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={chipStyle(!filtroCategoria)} onClick={() => setFiltroCategoria('')}>Todas</span>
                {CATEGORIAS.map(cat => (
                  <span key={cat} style={chipStyle(filtroCategoria === cat)} onClick={() => setFiltroCategoria(filtroCategoria === cat ? '' : cat)}>
                    {cat}
                  </span>
                ))}
              </div>
            </div>

            {/* Nível de Risco */}
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#78909c', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Nível de Risco
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={chipStyle(!filtroNivel)} onClick={() => setFiltroNivel('')}>Todos</span>
                {NIVEIS.map(n => {
                  const nc = CORES_NIVEL_CURVA[n] || {};
                  const ativo = filtroNivel === n;
                  return (
                    <span key={n} style={{
                      ...chipStyle(ativo),
                      background: ativo ? nc.color : '#fff',
                      borderColor: ativo ? nc.color : '#cfd8dc',
                      color: ativo ? '#fff' : nc.color,
                    }} onClick={() => setFiltroNivel(filtroNivel === n ? '' : n)}>
                      {n}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Tipo de Evidência + Multimorbidade + Custo — em linha */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#78909c', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Tipo de Evidência
                </div>
                <Tip text="Tratamento ativo = medicação contínua ou procedimento terapêutico confirmado.\nApenas rastreio = somente exames preventivos (rotina).">
                  <select style={selectStyle} value={filtroEvidencia} onChange={e => setFiltroEvidencia(e.target.value)}>
                    {EVIDENCIAS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </select>
                </Tip>
              </div>
              <div>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#78909c', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Multimorbidade
                </div>
                <Tip text="Quantidade mínima de áreas clínicas simultâneas ativas.\n2+ = comorbidade  ·  3+ = multimorbidade  ·  4+ = complexo">
                  <select style={selectStyle} value={filtroMulti} onChange={e => setFiltroMulti(e.target.value)}>
                    {MULTIMORBIDADE.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </Tip>
              </div>
              <div>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#78909c', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Custo Mensal Médio
                </div>
                <Tip text="Filtra por faixa de custo médio mensal do beneficiário.\nÚtil para separar alto custo de baixo custo dentro da mesma linha de cuidado.">
                  <select style={selectStyle} value={filtroCusto} onChange={e => setFiltroCusto(e.target.value)}>
                    {FAIXAS_CUSTO.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </Tip>
              </div>

              {filtrosAtivos > 0 && (
                <button onClick={limparFiltros} style={{
                  fontSize: '0.74rem', fontWeight: 600, padding: '6px 14px', borderRadius: 8,
                  border: '1.5px solid #ef5350', background: '#fff', color: '#c62828',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 1,
                }}>
                  <RefreshCw size={12} /> Limpar filtros
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {erro && <ErroBanner msg={erro} />}
      {loading && <LoadingBanner msg="Analisando trajetórias clínicas…" sub="Classificando procedimentos e medicamentos em categorias de risco" />}

      {!loading && dados && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: '1rem' }}>
            <KpiCard icon={AlertTriangle} label="Crítico" value={dados.por_nivel?.['Crítico'] || 0} color="#b71c1c"
              tip="Multimorbidade severa + custo alto: ação urgente"
              onClick={() => setFiltroNivel(filtroNivel === 'Crítico' ? '' : 'Crítico')} />
            <KpiCard icon={TrendingUp} label="Alto" value={dados.por_nivel?.['Alto'] || 0} color="#e65100"
              tip="Múltiplas áreas clínicas ativas + custo elevado"
              onClick={() => setFiltroNivel(filtroNivel === 'Alto' ? '' : 'Alto')} />
            <KpiCard icon={Activity} label="Moderado" value={dados.por_nivel?.['Moderado'] || 0} color="#f57f17"
              tip="Categorias clínicas presentes: acompanhar"
              onClick={() => setFiltroNivel(filtroNivel === 'Moderado' ? '' : 'Moderado')} />
            <KpiCard icon={Info} label="Atenção" value={dados.por_nivel?.['Atenção'] || 0} color="#2e7d32"
              tip="Sinais iniciais em 1-2 categorias"
              onClick={() => setFiltroNivel(filtroNivel === 'Atenção' ? '' : 'Atenção')} />
            <KpiCard icon={Users} label="Saudável" value={dados.por_nivel?.['Saudável'] || 0} color="#78909c"
              tip="Baixo risco baseado nos procedimentos atuais"
              onClick={() => setFiltroNivel(filtroNivel === 'Saudável' ? '' : 'Saudável')} />
          </div>

          {alertas.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {alertas.map((a) => (
                <AlertaRow key={a.matricula_soul} alerta={a} />
              ))}
            </div>
          )}

          {alertas.length === 0 && (
            <div style={{ background: '#fff', borderRadius: 12, padding: '3rem', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <CheckCircle2 size={32} color="#a5d6a7" style={{ marginBottom: 12 }} />
              <div style={{ fontSize: '0.9rem', color: 'var(--gray-500)' }}>
                {busca ? 'Nenhum resultado para esta busca.' : filtrosAtivos > 0 ? 'Nenhum resultado para os filtros selecionados.' : 'Nenhum alerta detectado.'}
              </div>
            </div>
          )}

          <TableFooter count={alertas.length}
            note="Fonte: crm.esau_utilizacao (36 meses): score por perfil clínico, custo e crescimento sobre a massa completa da carteira" />
        </>
      )}
    </div>
  );
}

// ── Componentes utilitários ───────────────────────────────────────────────────
function Th({ label, tip, center }) {
  return (
    <th style={{
      padding: '10px 12px',
      textAlign: center ? 'center' : 'left',
      fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-500)',
      textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>
      {tip ? <Tip text={tip}><span style={{ borderBottom: '1px dashed var(--gray-300)', cursor: 'help' }}>{label}</span></Tip> : label}
    </th>
  );
}

function TableFooter({ count, note }) {
  const { branding } = useBranding();
  const notaLimpa = sanitizarTooltip(note, branding?.demo);
  return (
    <div style={{ padding: '10px 16px', fontSize: '0.75rem', color: 'var(--gray-400)', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
      <span>{count} registro{count !== 1 ? 's' : ''}</span>
      {notaLimpa && (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Database size={11} /> {notaLimpa}
        </span>
      )}
    </div>
  );
}

function ErroBanner({ msg }) {
  return (
    <div style={{ background: '#ffebee', borderRadius: 10, padding: '1rem 1.2rem', color: '#c62828', fontSize: '0.85rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
      <AlertTriangle size={16} /> {msg}
    </div>
  );
}

function LoadingBanner({ msg, sub }) {
  const { branding } = useBranding();
  const subLimpo = sanitizarTooltip(sub, branding?.demo);
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '3rem', textAlign: 'center', color: 'var(--gray-400)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <Loader size={28} className="spin" style={{ marginBottom: 12 }} />
      <div style={{ fontSize: '0.9rem' }}>{msg}</div>
      {subLimpo && <div style={{ fontSize: '0.78rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}><Database size={12} /> {subLimpo}</div>}
    </div>
  );
}

function VazioMsg() {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '3rem', textAlign: 'center', color: 'var(--gray-400)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <CheckCircle2 size={32} style={{ marginBottom: 12, color: '#a5d6a7' }} />
      <div>Nenhum registro encontrado para os filtros selecionados.</div>
    </div>
  );
}

// ── AbaSaudeCorporativa — Dashboard Empresarial ──────────────────────────────

import {
  PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend as RLegend,
  Treemap,
} from 'recharts';

const CORES_PIRAMIDE = {
  'Crítico': '#c62828', 'Alto': '#e65100', 'Moderado': '#f9a825',
  'Atenção': '#1565c0', 'Saudável': '#2e7d32',
};
const ORDEM_NIVEL = ['Crítico', 'Alto', 'Moderado', 'Atenção', 'Saudável'];

const CORES_DOENCA = {
  'Diabetes': '#e65100', 'Cardiovascular': '#b71c1c', 'Autoimune/Inflamatório': '#6a1b9a',
  'Renal': '#00695c', 'Tireoide': '#1565c0', 'Oncológico': '#880e4f',
  'Saúde Mental': '#4527a0', 'Dislipidemia': '#ef6c00', 'Internação/Urgência': '#c62828',
  'TEA/Neurodesenvolvimento': '#00838f', 'Fonoaudiologia': '#558b2f',
  'Fisioterapia/Reabilitação': '#2e7d32', 'Ortopédico/Coluna': '#5d4037',
  'Materno-Infantil': '#ad1457', 'Obesidade/Bariátrica': '#ff6f00',
};
const CORES_FAIXA = {
  '0-12': '#00838f', '13-18': '#0097a7', '19-35': '#43a047',
  '36-50': '#f9a825', '51-65': '#ef6c00', '65+': '#c62828',
};
const CORES_DOENCA_ARR = Object.entries(CORES_DOENCA);

// Tooltip customizado para gráficos recharts
function ChartTooltip({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8,
      padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', fontSize: '0.78rem',
    }}>
      {label && <div style={{ fontWeight: 700, marginBottom: 4, color: '#333' }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, color: p.color || '#333' }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color || p.fill }} />
          <span>{p.name}: </span>
          <strong>{formatter ? formatter(p.value) : p.value.toLocaleString('pt-BR')}</strong>
        </div>
      ))}
    </div>
  );
}

// Painel de drill-down (beneficiários)
function PainelDrillDown({ filtros, onClose }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandido, setExpandido] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const resp = await api.get('/radar-carteira/saude-corporativa/detalhe', { params: filtros });
        if (!cancelled) setDados(resp.data);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [filtros]);

  const titulo = [
    filtros.sem_empresa && 'Sem Empresa (investigar)',
    filtros.empresa && `${filtros.empresa}`,
    filtros.nivel_risco && `Risco: ${filtros.nivel_risco}`,
    filtros.regiao && `Região: ${filtros.regiao}`,
    filtros.categoria && `${filtros.categoria}`,
    filtros.prestador && `${filtros.prestador}`,
  ].filter(Boolean).join(' · ') || 'Detalhe';

  const corRisco = (r) => CORES_PIRAMIDE[r] || '#999';
  const corConfianca = (n) => {
    if (n === 'Alta') return { bg: '#e8f5e9', fg: '#2e7d32', border: '#a5d6a7' };
    if (n === 'Média') return { bg: '#fff8e1', fg: '#f57c00', border: '#ffcc80' };
    return { bg: '#fff3e0', fg: '#bf360c', border: '#ffab91' }; // Baixa
  };

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, width: 580, height: '100vh',
      background: '#f0faf5', boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
      zIndex: 1000, overflowY: 'auto', padding: 0,
      borderLeft: '4px solid var(--brand, #20a490)',
    }}>
      {/* Header fixo */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 2,
        background: 'linear-gradient(135deg, var(--brand, #20a490), #1a8a7a)',
        padding: '16px 20px', color: '#fff',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: '0.92rem', fontWeight: 700, margin: 0 }}>{titulo}</h3>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6,
            padding: '5px 12px', cursor: 'pointer', fontSize: '0.78rem',
            fontWeight: 600, color: '#fff',
          }}>Fechar</button>
        </div>
        {dados && (
          <div style={{ fontSize: '0.75rem', marginTop: 6, opacity: 0.9 }}>
            {dados.total} beneficiário{dados.total !== 1 ? 's' : ''} · ordenados por custo total
          </div>
        )}
      </div>

      <div style={{ padding: '16px 20px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Loader size={22} className="spin" style={{ color: 'var(--brand, #20a490)' }} />
            <div style={{ marginTop: 8, fontSize: '0.78rem', color: '#888' }}>Buscando beneficiários...</div>
          </div>
        ) : !dados?.beneficiarios?.length ? (
          <div style={{ color: '#999', padding: '2rem', textAlign: 'center', fontSize: '0.82rem' }}>
            Nenhum beneficiário encontrado para estes filtros.
          </div>
        ) : (
          dados.beneficiarios.map((b, idx) => {
            const aberto = expandido[b.matricula];
            return (
              <div key={b.matricula} style={{
                border: '1px solid #d4ede6', borderRadius: 10, marginBottom: 8,
                background: '#fff', overflow: 'hidden',
              }}>
                {/* Header do beneficiário */}
                <div
                  onClick={() => setExpandido(prev => ({ ...prev, [b.matricula]: !prev[b.matricula] }))}
                  style={{
                    padding: '10px 14px', cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>{b.nome}</span>
                      {b.risco_ml && (
                        <span style={{
                          fontSize: '0.62rem', padding: '1px 6px', borderRadius: 10,
                          background: corRisco(b.risco_ml) + '20', color: corRisco(b.risco_ml),
                          fontWeight: 700,
                        }}>{b.risco_ml}{b.prob_ml != null ? ` ${b.prob_ml}%` : ''}</span>
                      )}
                      {b.nivel_confianca && (
                        <Tip text={`Confiança nos dados: ${b.nivel_confianca} (${b.confianca_dados}%)\n\nDerivada de:\n• Histórico de uso\n• Dispersão temporal do custo\n• Recorrência mensal\n\nBaixa/Média = predição apoiada em pouco dado ou evento concentrado. Investigar antes de agir.`}>
                          <span style={{
                            fontSize: '0.6rem', padding: '1px 6px', borderRadius: 10,
                            background: corConfianca(b.nivel_confianca).bg,
                            color: corConfianca(b.nivel_confianca).fg,
                            border: `1px solid ${corConfianca(b.nivel_confianca).border}`,
                            fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3,
                          }}>
                            {b.nivel_confianca === 'Alta' ? '●●●' : b.nivel_confianca === 'Média' ? '●●○' : '●○○'} {b.nivel_confianca}
                          </span>
                        </Tip>
                      )}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#888', marginTop: 2 }}>
                      Mat: {b.matricula} · {b.empresa || '(Sem empresa)'}{b.idade ? ` · ${b.idade} anos` : ''}
                    </div>
                    {/* Categorias clínicas inline */}
                    {b.categorias?.length > 0 && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                        {b.categorias.map(c => (
                          <span key={c.categoria} style={{
                            fontSize: '0.62rem', padding: '1px 6px', borderRadius: 8,
                            background: (c.cor || '#999') + '18', color: c.cor || '#666',
                            fontWeight: 600,
                          }}>
                            {c.categoria} ({c.tratamento}T{c.rastreio > 0 ? `+${c.rastreio}R` : ''})
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#e65100' }}>
                        R$ {b.custo_total.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                      </div>
                      <div style={{ fontSize: '0.66rem', color: '#999' }}>
                        {b.eventos_total.toLocaleString('pt-BR')} ev · {b.n_prestadores} prest
                      </div>
                      {/* Fatia da categoria quando há filtro ativo */}
                      {b.custo_categoria != null && (
                        <Tip text={`Este beneficiário gastou R$ ${b.custo_categoria.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} em ${b.eventos_categoria} evento(s) da categoria filtrada.\n\nOs números maiores acima são o custo TOTAL dele (todas as categorias).`}>
                          <div style={{
                            fontSize: '0.62rem', color: '#1565c0', marginTop: 2,
                            background: '#e3f2fd', padding: '1px 6px', borderRadius: 4,
                            display: 'inline-block', cursor: 'help',
                          }}>
                            Categoria: R$ {b.custo_categoria.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} ({b.eventos_categoria} ev)
                          </div>
                        </Tip>
                      )}
                      {/* Badge de farmácia opaca — quando o dado é insuficiente */}
                      {b.pct_farmacia_opaca >= 30 && (
                        <Tip text={`${b.pct_farmacia_opaca}% do custo deste beneficiário está em "MEDICAMENTOS FARMACIA": dispensações da Funcional Card que chegam agregadas, sem o nome do medicamento.\n\nIsso limita a classificação clínica automática: o modelo vê o valor alto mas não consegue identificar qual doença está sendo tratada.\n\nPara enriquecer: solicitar extrato detalhado da Funcional Card.`}>
                          <div style={{
                            fontSize: '0.6rem', color: '#bf360c', marginTop: 2,
                            background: '#fff3e0', border: '1px solid #ffab91',
                            padding: '1px 6px', borderRadius: 4,
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            cursor: 'help',
                          }}>
                            <AlertTriangle size={9} /> {b.pct_farmacia_opaca}% farmácia opaca
                          </div>
                        </Tip>
                      )}
                    </div>
                    {aberto ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </div>
                </div>

                {/* Expandido: top procedimentos por custo */}
                {aberto && (
                  <div style={{ padding: '0 14px 14px', borderTop: '1px solid #e8f5ef' }}>
                    {b.prestadores?.length > 0 && (
                      <div style={{ fontSize: '0.7rem', color: '#666', marginTop: 8, marginBottom: 8 }}>
                        <strong>Prestadores:</strong> {b.prestadores.join(', ')}
                      </div>
                    )}
                    <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#555', marginBottom: 6 }}>
                      Top procedimentos por custo:
                    </div>
                    <table style={{ width: '100%', fontSize: '0.7rem', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ color: '#999', borderBottom: '1px solid #eee' }}>
                          <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Descrição</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Qtd</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Custo Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(b.top_procedimentos || []).map((p, pi) => (
                          <tr key={pi} style={{ borderBottom: '1px solid #f5f5f5' }}>
                            <td style={{
                              padding: '4px 6px', maxWidth: 300, overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {p.descricao}
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', color: '#888' }}>
                              x{p.qtd}
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>
                              R$ {p.custo.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function AbaSaudeCorporativa() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [empresaSel, setEmpresaSel] = useState('');
  const [drillDown, setDrillDown] = useState(null);
  const [expandReg, setExpandReg] = useState({});
  // modoDoenca removido — tabela unificada exibe todas as dimensões ao mesmo tempo

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (empresaSel) params.empresa = empresaSel;
      const resp = await api.get('/radar-carteira/saude-corporativa', { params });
      setDados(resp.data);
    } catch (e) {
      console.error('Erro saude-corporativa:', e);
    } finally {
      setLoading(false);
    }
  }, [empresaSel]);

  useEffect(() => { carregar(); }, [carregar]);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <Loader size={28} className="spin" style={{ color: 'var(--primary)' }} />
      <div style={{ marginTop: 8, color: 'var(--gray-500)', fontSize: '0.85rem' }}>
        Analisando dados de utilização... (pode levar até 40s)
      </div>
    </div>
  );

  if (!dados) return <div style={{ padding: '2rem', color: '#999' }}>Sem dados disponíveis.</div>;

  const { empresas, resumo_empresas, regioes, prestadores_top, doencas_regiao, total_ativos, benchmark } = dados;
  const totalAtivos = total_ativos || 0;

  // Empresa selecionada (ou visão geral se nenhuma)
  const empAtual = empresaSel
    ? (resumo_empresas || []).find(e => e.empresa === empresaSel)
    : null;

  // Dados da empresa selecionada ou consolidado
  const piramide = empAtual ? (empAtual.piramide || {}) : (() => {
    const p = {};
    (resumo_empresas || []).forEach(e => {
      Object.entries(e.piramide || {}).forEach(([nv, qtd]) => { p[nv] = (p[nv] || 0) + qtd; });
    });
    return p;
  })();
  const totalPir = Object.values(piramide).reduce((s, v) => s + v, 0);

  const doencasAtual = empAtual ? (empAtual.doencas || {}) : (() => {
    const d = {};
    (resumo_empresas || []).forEach(e => {
      Object.entries(e.doencas || {}).forEach(([cat, qtd]) => { d[cat] = (d[cat] || 0) + qtd; });
    });
    return d;
  })();

  // Custo total por categoria (novo — backend retorna custo_categoria)
  const custoCatAtual = empAtual ? (empAtual.custo_categoria || {}) : (() => {
    const d = {};
    (resumo_empresas || []).forEach(e => {
      Object.entries(e.custo_categoria || {}).forEach(([cat, v]) => { d[cat] = (d[cat] || 0) + v; });
    });
    return d;
  })();

  // Prevalência (pessoas) por categoria — já existia mas precisamos aqui
  const prevAtualDoenca = empAtual ? (empAtual.prevalencia || {}) : (() => {
    const d = {};
    (resumo_empresas || []).forEach(e => {
      Object.entries(e.prevalencia || {}).forEach(([cat, v]) => { d[cat] = (d[cat] || 0) + v; });
    });
    return d;
  })();

  // Custo por pessoa por categoria (prefere dado do backend, calcula no frontend como fallback)
  const custoPorPessoaCat = empAtual
    ? (empAtual.custo_por_pessoa || {})
    : (() => {
        const d = {};
        (resumo_empresas || []).forEach(e => {
          Object.entries(e.custo_por_pessoa || {}).forEach(([cat, v]) => {
            d[cat] = d[cat] == null ? v : (d[cat] + v) / 2; // média simples no consolidado
          });
        });
        return d;
      })();

  // Lista de categorias ordenadas por custo total (critério principal)
  const todasCatDoenca = Array.from(new Set([
    ...Object.keys(doencasAtual),
    ...Object.keys(custoCatAtual),
    ...Object.keys(prevAtualDoenca),
  ])).filter(cat => (doencasAtual[cat] || 0) > 0 || (custoCatAtual[cat] || 0) > 0);

  const doencasSorted = todasCatDoenca
    .map(cat => [cat, custoCatAtual[cat] || 0])
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);

  const totalDoencas = Object.values(doencasAtual).reduce((s, v) => s + v, 0);
  const totalCustoCat = Object.values(custoCatAtual).reduce((s, v) => s + v, 0);

  const pieRisco = ORDEM_NIVEL
    .filter(nv => (piramide[nv] || 0) > 0)
    .map(nv => ({ name: nv, value: piramide[nv] || 0, fill: CORES_PIRAMIDE[nv] }));

  const custoAtual = empAtual ? empAtual.custo_total : (resumo_empresas || []).reduce((s, e) => s + e.custo_total, 0);
  const benefAtual = empAtual ? empAtual.beneficiarios_com_uso : (resumo_empresas || []).reduce((s, e) => s + e.beneficiarios_com_uso, 0);
  const custoPerCapita = empAtual ? empAtual.custo_per_capita : (benefAtual > 0 ? Math.round(custoAtual / benefAtual) : 0);

  // Prevalência: % de beneficiários afetados por categoria (epidemiologia)
  const prevalenciaAtual = empAtual ? (empAtual.prevalencia || {}) : (() => {
    const p = {};
    (resumo_empresas || []).forEach(e => {
      Object.entries(e.prevalencia || {}).forEach(([cat, qtd]) => { p[cat] = (p[cat] || 0) + qtd; });
    });
    return p;
  })();
  const vidasEmpresa = empAtual
    ? ((empresas || []).find(e => e.nome === empresaSel)?.total || 0)
    : totalAtivos;
  const prevalenciaSorted = Object.entries(prevalenciaAtual)
    .sort((a, b) => b[1] - a[1]);
  const piePrevalencia = prevalenciaSorted
    .map(([cat, qtd]) => ({
      name: cat, value: qtd,
      fill: CORES_DOENCA[cat] || '#999',
      pct: vidasEmpresa > 0 ? ((qtd / vidasEmpresa) * 100).toFixed(1) : '0',
    }));

  // Faixa etária
  const faixaAtual = empAtual ? (empAtual.faixa_etaria || {}) : (() => {
    const f = {};
    (resumo_empresas || []).forEach(e => {
      Object.entries(e.faixa_etaria || {}).forEach(([fx, qtd]) => { f[fx] = (f[fx] || 0) + qtd; });
    });
    return f;
  })();
  const ORDEM_FAIXA = ['0-12', '13-18', '19-35', '36-50', '51-65', '65+'];
  const barFaixa = ORDEM_FAIXA.filter(f => (faixaAtual[f] || 0) > 0)
    .map(f => ({ name: f, value: faixaAtual[f] || 0, fill: CORES_FAIXA[f] }));
  const totalFaixa = barFaixa.reduce((s, v) => s + v.value, 0);

  const fmt = (v) => 'R$ ' + (v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 });

  // Drill-down helper
  const drillEmpresa = (extra = {}) => {
    if (empAtual) {
      setDrillDown({ empresa: empAtual.empresa, ...extra });
    } else {
      setDrillDown(extra);
    }
  };

  return (
    <div>
      {drillDown && <PainelDrillDown filtros={drillDown} onClose={() => setDrillDown(null)} />}

      {/* SELETOR DE EMPRESA — proeminente */}
      <div style={{
        background: 'linear-gradient(135deg, #f8f9fa, #eef2f7)', borderRadius: 12,
        padding: '16px 20px', marginBottom: '1.2rem',
        border: '1px solid #dde3ea',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <Building2 size={22} style={{ color: 'var(--primary)' }} />
          <div>
            <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: 2 }}>Selecione a empresa cliente</div>
            <select
              value={empresaSel}
              onChange={e => { setEmpresaSel(e.target.value); setExpandReg({}); }}
              style={{
                padding: '8px 16px', borderRadius: 8, border: '2px solid var(--primary)',
                fontSize: '0.9rem', background: '#fff', minWidth: 350, fontWeight: 600,
                color: '#333',
              }}
            >
              <option value="">Visão Geral: todas as empresas</option>
              {(empresas || []).filter(e => e.nome !== '(Sem empresa)').map(e => (
                <option key={e.nome} value={e.nome}>
                  {e.nome} ({e.total.toLocaleString('pt-BR')} vidas)
                </option>
              ))}
            </select>
          </div>

          {/* KPIs inline */}
          <div style={{ display: 'flex', gap: 20, marginLeft: 'auto', flexWrap: 'wrap' }}>
            {(() => {
              const vidasKpi = (empAtual ? (empresas || []).find(e => e.nome === empresaSel)?.total : totalAtivos) || 0;
              const pctUso = vidasKpi > 0 ? Math.round((benefAtual / vidasKpi) * 100) : 0;
              const altosRisco = (() => {
                const p = empAtual ? (empAtual.piramide || {}) : piramide;
                return (p['Crítico'] || 0) + (p['Alto'] || 0);
              })();
              const totalPirKpi = Object.values(empAtual ? (empAtual.piramide || {}) : piramide).reduce((s, v) => s + v, 0);
              const pctAltoRisco = totalPirKpi > 0 ? ((altosRisco / totalPirKpi) * 100).toFixed(1) : '—';
              return [
                { label: 'Vidas', value: vidasKpi.toLocaleString('pt-BR'), color: 'var(--primary)' },
                { label: '% com utilização', value: `${pctUso}%`, color: '#2e7d32' },
                { label: 'Custo Total', value: fmt(custoAtual), color: '#e65100' },
                { label: 'Custo/Vida', value: fmt(custoPerCapita), color: '#6a1b9a' },
                { label: '% alto risco (modelo)', value: `${pctAltoRisco}%`, color: '#c62828' },
              ].map((kpi, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, fontSize: '1.05rem', color: kpi.color }}>{kpi.value}</div>
                  <div style={{ fontSize: '0.68rem', color: '#888' }}>{kpi.label}</div>
                </div>
              ));
            })()}
          </div>
        </div>

        {/* Visão geral: cards de empresas clicáveis */}
        {!empresaSel && (
          <div style={{ marginTop: 14 }}>
            {/* Ranking por custo/vida — normalizado pelo tamanho da empresa */}
            {(() => {
              // Média do portfólio: custo total / beneficiários com uso (excluindo sem empresa)
              const totalCustoPort = (resumo_empresas || []).filter(r => r.empresa !== '(Sem empresa)').reduce((s, r) => s + r.custo_total, 0);
              const totalBenefPort = (resumo_empresas || []).filter(r => r.empresa !== '(Sem empresa)').reduce((s, r) => s + r.beneficiarios_com_uso, 0);
              const mediaPortfolio = totalBenefPort > 0 ? totalCustoPort / totalBenefPort : 0;

              // Montar lista unificada com custo/vida, ordenada de maior para menor
              const lista = (resumo_empresas || [])
                .filter(r => r.beneficiarios_com_uso >= 1)
                .map(r => {
                  const vidasTotal = (empresas || []).find(e => e.nome === r.empresa)?.total || r.beneficiarios_com_uso;
                  return { ...r, vidasTotal };
                })
                .sort((a, b) => b.custo_per_capita - a.custo_per_capita);

              const semEmpresa = lista.find(r => r.empresa === '(Sem empresa)');
              const listaNormal = lista.filter(r => r.empresa !== '(Sem empresa)');

              return (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {listaNormal.map(r => {
                    const ratio = mediaPortfolio > 0 ? r.custo_per_capita / mediaPortfolio : 1;
                    const corBorda = ratio >= 1.5 ? '#c62828' : ratio >= 1.1 ? '#ef6c00' : ratio <= 0.9 ? '#2e7d32' : '#ddd';
                    const corCusto = ratio >= 1.5 ? '#c62828' : ratio >= 1.1 ? '#e65100' : '#6a1b9a';
                    const tag = ratio >= 1.5 ? `↑ ${ratio.toFixed(1)}x` : ratio >= 1.1 ? `↑ ${ratio.toFixed(1)}x` : ratio <= 0.9 ? `↓ ${ratio.toFixed(1)}x` : '≈ média';
                    return (
                      <button key={r.empresa}
                        onClick={() => setEmpresaSel(r.empresa)}
                        style={{
                          background: '#fff', border: `1.5px solid ${corBorda}`,
                          borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
                          textAlign: 'left', minWidth: 175,
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: '0.76rem', color: '#333', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 }}>
                          {r.empresa.length > 28 ? r.empresa.slice(0, 26) + '...' : r.empresa}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: corCusto }}>
                            {fmt(r.custo_per_capita)}<span style={{ fontSize: '0.6rem', fontWeight: 400, color: '#aaa' }}>/vida</span>
                          </span>
                          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: corCusto }}>{tag}</span>
                        </div>
                        <div style={{ fontSize: '0.64rem', color: '#bbb', marginTop: 2 }}>
                          {r.vidasTotal.toLocaleString('pt-BR')} vidas · {fmt(r.custo_total)} total
                        </div>
                      </button>
                    );
                  })}
                  {semEmpresa && (
                    <button onClick={() => setDrillDown({ sem_empresa: true })}
                      style={{ background: '#fff3e0', border: '1px solid #e65100', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', textAlign: 'left', minWidth: 175 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.76rem', color: '#e65100', marginBottom: 2 }}>
                        <AlertTriangle size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                        Sem Empresa (investigar)
                      </div>
                      <div style={{ fontSize: '0.64rem', color: '#aaa' }}>
                        {semEmpresa.beneficiarios_com_uso} benef · {fmt(semEmpresa.custo_total)}
                      </div>
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* DASHBOARD DA EMPRESA SELECIONADA */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem', marginBottom: '1.2rem' }}>

        {/* Pizza: Pirâmide de Risco */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.2rem', border: '1px solid #e8e8e8' }}>
          <h3 style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: 4, color: '#333' }}>
            <ShieldAlert size={15} style={{ marginRight: 6, verticalAlign: -2 }} />
            Pirâmide de Risco
          </h3>
          <div style={{ fontSize: '0.7rem', color: '#999', marginBottom: 8 }}>
            {totalPir > 0 ? `${totalPir} beneficiários classificados pelo modelo preditivo` : 'Sem dados do modelo preditivo disponíveis'}
          </div>
          {totalPir > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieRisco} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" innerRadius={50} outerRadius={85}
                    paddingAngle={2} cursor="pointer"
                    onClick={(_, idx) => { if (pieRisco[idx]) drillEmpresa({ nivel_risco: pieRisco[idx].name }); }}
                  >
                    {pieRisco.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <RTooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0];
                    const pct = ((d.value / totalPir) * 100).toFixed(1);
                    return (
                      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', fontSize: '0.78rem' }}>
                        <div style={{ fontWeight: 700, color: d.payload.fill }}>{d.name}</div>
                        <div>{d.value.toLocaleString('pt-BR')} beneficiários ({pct}%)</div>
                      </div>
                    );
                  }} />
                </PieChart>
              </ResponsiveContainer>
              {/* Legenda com barras */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                {ORDEM_NIVEL.map(nv => {
                  const qtd = piramide[nv] || 0;
                  if (qtd === 0) return null;
                  const pct = ((qtd / totalPir) * 100).toFixed(1);
                  return (
                    <div key={nv} onClick={() => drillEmpresa({ nivel_risco: nv })} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', borderRadius: 4, padding: '2px 4px', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontSize: '0.72rem', width: 65, color: CORES_PIRAMIDE[nv], fontWeight: 700 }}>{nv}</span>
                      <div style={{ flex: 1, height: 12, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: CORES_PIRAMIDE[nv], borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: '0.72rem', color: '#555', minWidth: 70, textAlign: 'right' }}>
                        {qtd.toLocaleString('pt-BR')} ({pct}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#ccc' }}>
              <ShieldAlert size={32} />
              <div style={{ marginTop: 8 }}>Execute o modelo preditivo para gerar classificações</div>
            </div>
          )}
        </div>

        {/* Card: Tabela unificada de Doenças — todas as dimensões visíveis */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.2rem', border: '1px solid #e8e8e8' }}>
          <div style={{ marginBottom: 10 }}>
            <h3 style={{ fontSize: '0.88rem', fontWeight: 700, margin: 0, color: '#333' }}>
              <Activity size={15} style={{ marginRight: 6, verticalAlign: -2 }} />
              Doenças: Tratamentos Ativos
            </h3>
            <div style={{ fontSize: '0.68rem', color: '#999', marginTop: 2 }}>
              {totalCustoCat > 0
                ? `${fmt(totalCustoCat)} · ${doencasSorted.length} categorias · ${totalDoencas.toLocaleString('pt-BR')} procedimentos`
                : `${doencasSorted.length} categorias · ${totalDoencas.toLocaleString('pt-BR')} procedimentos`}
            </div>
          </div>

          {doencasSorted.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                    <th style={{ textAlign: 'left', padding: '4px 6px', color: '#888', fontWeight: 600, width: '30%' }}>Categoria</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', color: '#888', fontWeight: 600 }}>Pessoas</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', color: '#888', fontWeight: 600 }}>Sessões</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', color: '#888', fontWeight: 600 }}>Custo Total</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', color: '#888', fontWeight: 600 }}>R$/Pessoa</th>
                    {benchmark && Object.keys(benchmark).length > 0 && (
                      <th style={{ textAlign: 'center', padding: '4px 6px', color: '#888', fontWeight: 600 }}>vs Portfólio</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {doencasSorted.map(([cat, custoTotal]) => {
                    const sessoes = doencasAtual[cat] || 0;
                    const pessoas = prevAtualDoenca[cat] || 0;
                    const custoPessoa = custoPorPessoaCat[cat] || (pessoas > 0 ? custoTotal / pessoas : 0);
                    const benchmarkCat = benchmark?.[cat];
                    const cor = CORES_DOENCA[cat] || '#999';

                    // Ratio vs benchmark: >1.5x vermelho, >1.1x laranja, <=0.9x verde, senão neutro
                    let benchmarkEl = null;
                    if (benchmarkCat && custoPessoa > 0) {
                      const ratio = custoPessoa / benchmarkCat;
                      let corBench, label;
                      if (ratio >= 1.5) { corBench = '#c62828'; label = `↑ ${ratio.toFixed(1)}x`; }
                      else if (ratio >= 1.1) { corBench = '#e65100'; label = `↑ ${ratio.toFixed(1)}x`; }
                      else if (ratio <= 0.9) { corBench = '#2e7d32'; label = `↓ ${ratio.toFixed(1)}x`; }
                      else { corBench = '#888'; label = '≈ média'; }
                      benchmarkEl = (
                        <span title={`Média portfólio: ${fmt(benchmarkCat)}/pessoa`}
                          style={{ fontSize: '0.66rem', fontWeight: 700, color: corBench, whiteSpace: 'nowrap' }}>
                          {label}
                        </span>
                      );
                    }

                    return (
                      <tr key={cat}
                        onClick={() => drillEmpresa({ categoria: cat })}
                        style={{ borderBottom: '1px solid #f5f5f5', cursor: 'pointer', transition: 'background 0.12s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fffe'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '6px 6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 8, height: 8, borderRadius: 2, background: cor, flexShrink: 0 }} />
                            <span style={{ fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {cat}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '6px 6px', textAlign: 'right', color: '#555' }}>
                          {pessoas > 0 ? pessoas.toLocaleString('pt-BR') : '—'}
                        </td>
                        <td style={{ padding: '6px 6px', textAlign: 'right', color: '#555' }}>
                          {sessoes > 0 ? sessoes.toLocaleString('pt-BR') : '—'}
                        </td>
                        <td style={{ padding: '6px 6px', textAlign: 'right', fontWeight: 700, color: '#e65100' }}>
                          {custoTotal > 0 ? fmt(custoTotal) : '—'}
                        </td>
                        <td style={{ padding: '6px 6px', textAlign: 'right', color: '#6a1b9a', fontWeight: 600 }}>
                          {custoPessoa > 0 ? fmt(custoPessoa) : '—'}
                        </td>
                        {benchmark && Object.keys(benchmark).length > 0 && (
                          <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                            {benchmarkEl || <span style={{ color: '#ccc', fontSize: '0.66rem' }}>—</span>}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ textAlign: 'center', fontSize: '0.63rem', color: '#ccc', marginTop: 6 }}>
                Clique na linha para ver beneficiários · vs Portfólio = custo/pessoa vs média das empresas
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#ccc' }}>
              <Activity size={32} />
              <div style={{ marginTop: 8 }}>Sem dados</div>
            </div>
          )}
        </div>
      </div>

      {/* ROW 1.5: Prevalência Epidemiológica + Faixa Etária */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem', marginBottom: '1.2rem' }}>

        {/* Card: Prevalência por Doença (% beneficiários afetados) */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.2rem', border: '1px solid #e8e8e8' }}>
          <h3 style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: 4, color: '#333' }}>
            <Activity size={15} style={{ marginRight: 6, verticalAlign: -2 }} />
            Prevalência Epidemiológica
          </h3>
          <div style={{ fontSize: '0.7rem', color: '#999', marginBottom: 8 }}>
            % de beneficiários com tratamento ativo por categoria · base: {vidasEmpresa.toLocaleString('pt-BR')} vidas
          </div>
          {piePrevalencia.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={piePrevalencia} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" innerRadius={55} outerRadius={90}
                    paddingAngle={2} cursor="pointer"
                    onClick={(_, idx) => {
                      if (piePrevalencia[idx]) drillEmpresa({ categoria: piePrevalencia[idx].name });
                    }}
                  >
                    {piePrevalencia.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <RTooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0];
                    return (
                      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', fontSize: '0.78rem' }}>
                        <div style={{ fontWeight: 700, color: d.payload.fill }}>{d.name}</div>
                        <div>{d.value.toLocaleString('pt-BR')} beneficiários ({d.payload.pct}%)</div>
                      </div>
                    );
                  }} />
                </PieChart>
              </ResponsiveContainer>
              {/* Barras de prevalência com % */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                {prevalenciaSorted.map(([cat, qtd]) => {
                  const pct = vidasEmpresa > 0 ? ((qtd / vidasEmpresa) * 100) : 0;
                  const isAlerta = pct >= 5;
                  return (
                    <div key={cat}
                      onClick={() => drillEmpresa({ categoria: cat })}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '2px 4px', borderRadius: 4, background: isAlerta ? '#fff3e0' : 'transparent' }}>
                      <span style={{
                        fontSize: '0.7rem', width: 140, color: CORES_DOENCA[cat] || '#666',
                        fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{cat}</span>
                      <div style={{ flex: 1, height: 14, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(pct * 2, 100)}%`, height: '100%', background: CORES_DOENCA[cat] || '#999', borderRadius: 3, transition: 'width 0.3s' }} />
                      </div>
                      <span style={{ fontSize: '0.72rem', color: isAlerta ? '#e65100' : '#555', fontWeight: isAlerta ? 700 : 400, minWidth: 80, textAlign: 'right' }}>
                        {qtd.toLocaleString('pt-BR')} ({pct.toFixed(1)}%)
                      </span>
                    </div>
                  );
                })}
              </div>
              <div style={{ textAlign: 'center', fontSize: '0.66rem', color: '#bbb', marginTop: 6 }}>
                Categorias com {'>'}5% da população ficam destacadas em laranja
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#ccc' }}>
              <Activity size={32} />
              <div style={{ marginTop: 8 }}>Sem dados de prevalência</div>
            </div>
          )}
        </div>

        {/* Card: Faixa Etária */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.2rem', border: '1px solid #e8e8e8' }}>
          <h3 style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: 4, color: '#333' }}>
            <Users size={15} style={{ marginRight: 6, verticalAlign: -2 }} />
            Distribuição Etária
          </h3>
          <div style={{ fontSize: '0.7rem', color: '#999', marginBottom: 8 }}>
            Perfil demográfico · {totalFaixa.toLocaleString('pt-BR')} beneficiários
          </div>
          {barFaixa.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barFaixa} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <RTooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0];
                    const pct = totalFaixa > 0 ? ((d.value / totalFaixa) * 100).toFixed(1) : '0';
                    return (
                      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', fontSize: '0.78rem' }}>
                        <div style={{ fontWeight: 700 }}>{d.payload.name} anos</div>
                        <div>{d.value.toLocaleString('pt-BR')} beneficiários ({pct}%)</div>
                      </div>
                    );
                  }} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {barFaixa.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {/* Destaque: concentração pediátrica e idosa */}
              {(() => {
                const ped = (faixaAtual['0-12'] || 0) + (faixaAtual['13-18'] || 0);
                const idoso = faixaAtual['65+'] || 0;
                const pctPed = totalFaixa > 0 ? ((ped / totalFaixa) * 100).toFixed(1) : '0';
                const pctIdoso = totalFaixa > 0 ? ((idoso / totalFaixa) * 100).toFixed(1) : '0';
                return (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <div style={{ flex: 1, background: '#e0f7fa', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: '#00838f' }}>{pctPed}%</div>
                      <div style={{ fontSize: '0.68rem', color: '#00695c' }}>Pediátrico (0-18)</div>
                      <div style={{ fontSize: '0.64rem', color: '#999' }}>{ped.toLocaleString('pt-BR')} vidas</div>
                    </div>
                    <div style={{ flex: 1, background: '#fbe9e7', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: '#c62828' }}>{pctIdoso}%</div>
                      <div style={{ fontSize: '0.68rem', color: '#bf360c' }}>Idoso (65+)</div>
                      <div style={{ fontSize: '0.64rem', color: '#999' }}>{idoso.toLocaleString('pt-BR')} vidas</div>
                    </div>
                    <div style={{ flex: 1, background: '#e8f5e9', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: '#2e7d32' }}>{(100 - parseFloat(pctPed) - parseFloat(pctIdoso)).toFixed(1)}%</div>
                      <div style={{ fontSize: '0.68rem', color: '#1b5e20' }}>Adulto (19-64)</div>
                      <div style={{ fontSize: '0.64rem', color: '#999' }}>{(totalFaixa - ped - idoso).toLocaleString('pt-BR')} vidas</div>
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#ccc' }}>
              <Users size={32} />
              <div style={{ marginTop: 8 }}>Sem dados demográficos</div>
            </div>
          )}
        </div>
      </div>

      {/* ROW 2: Regiões + Prestadores */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem', marginBottom: '1.2rem' }}>

        {/* Regiões geográficas */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.2rem', border: '1px solid #e8e8e8' }}>
          <h3 style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: 8, color: '#333' }}>
            <Target size={15} style={{ marginRight: 6, verticalAlign: -2 }} />
            Onde estão sendo tratados
            <span style={{ fontWeight: 400, fontSize: '0.68rem', color: '#999', marginLeft: 6 }}>via prestador</span>
          </h3>
          {Object.entries(regioes || {})
            .sort((a, b) => b[1].beneficiarios - a[1].beneficiarios)
            .map(([regiao, info]) => {
              const maxB = Object.values(regioes).reduce((m, r) => Math.max(m, r.beneficiarios), 1);
              const aberto = expandReg[regiao];
              const doencas = doencas_regiao[regiao] || {};
              const doencasSrt = Object.entries(doencas).sort((a, b) => b[1] - a[1]);
              return (
                <div key={regiao} style={{ marginBottom: 6 }}>
                  <div
                    onClick={() => setExpandReg(p => ({ ...p, [regiao]: !p[regiao] }))}
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}
                  >
                    <span style={{ fontWeight: 600, fontSize: '0.78rem', width: 100 }}>{regiao}</span>
                    <div style={{ flex: 1, height: 16, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        width: `${(info.beneficiarios / maxB) * 100}%`,
                        height: '100%', background: '#1565c0', borderRadius: 4,
                        display: 'flex', alignItems: 'center', paddingLeft: 6,
                      }}>
                        {info.beneficiarios > maxB * 0.15 && (
                          <span style={{ fontSize: '0.62rem', color: '#fff', fontWeight: 600 }}>
                            {info.beneficiarios.toLocaleString('pt-BR')}
                          </span>
                        )}
                      </div>
                    </div>
                    <span style={{ fontSize: '0.68rem', color: '#888', minWidth: 55, textAlign: 'right' }}>
                      {info.beneficiarios <= Object.values(regioes).reduce((m, r) => Math.max(m, r.beneficiarios), 1) * 0.15
                        ? info.beneficiarios.toLocaleString('pt-BR') : ''}
                    </span>
                    {aberto ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </div>
                  {aberto && doencasSrt.length > 0 && (
                    <div style={{ padding: '4px 0 8px 108px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: 2 }}>
                        {fmt(info.custo)} · {info.prestadores} prestadores
                      </div>
                      {doencasSrt.slice(0, 6).map(([cat, qtd]) => (
                        <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                          onClick={() => drillEmpresa({ regiao, categoria: cat })}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: CORES_DOENCA[cat] || '#999' }} />
                          <span style={{ fontSize: '0.7rem', flex: 1, color: '#555' }}>{cat}</span>
                          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: CORES_DOENCA[cat] || '#666' }}>
                            {qtd.toLocaleString('pt-BR')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        {/* Top Prestadores */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.2rem', border: '1px solid #e8e8e8' }}>
          <h3 style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: 8, color: '#333' }}>
            <Users size={15} style={{ marginRight: 6, verticalAlign: -2 }} />
            Top Prestadores
          </h3>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {(prestadores_top || []).map((p, i) => (
              <div key={i}
                onClick={() => setDrillDown({ prestador: p.nome, ...(empresaSel ? { empresa: empresaSel } : {}) })}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 8px', borderBottom: '1px solid #f5f5f5',
                  cursor: 'pointer', borderRadius: 4,
                  background: i % 2 === 0 ? '#fafafa' : '#fff',
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.nome}
                  </div>
                  <div style={{ fontSize: '0.66rem', color: '#1565c0' }}>{p.regiao}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600 }}>{p.beneficiarios.toLocaleString('pt-BR')} benef</div>
                  <div style={{ fontSize: '0.66rem', color: '#888' }}>{fmt(p.custo)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ROW 3: Se visão geral, comparativo rápido de empresas */}
      {!empresaSel && (resumo_empresas || []).length > 1 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.2rem', border: '1px solid #e8e8e8', marginBottom: '1.2rem' }}>
          <h3 style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: 12, color: '#333' }}>
            <BarChart2 size={15} style={{ marginRight: 6, verticalAlign: -2 }} />
            Comparativo de Empresas
          </h3>
          <ResponsiveContainer width="100%" height={Math.max(200, (resumo_empresas || []).length * 36)}>
            <BarChart
              data={(resumo_empresas || []).map(e => ({
                name: e.empresa.length > 25 ? e.empresa.slice(0, 22) + '...' : e.empresa,
                fullName: e.empresa,
                custo: Math.round(e.custo_total),
                beneficiarios: e.beneficiarios_com_uso,
                perCapita: e.custo_per_capita,
              }))}
              layout="vertical"
              margin={{ left: 10, right: 20, top: 5, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}
                style={{ fontSize: '0.68rem' }} />
              <YAxis type="category" dataKey="name" width={180} style={{ fontSize: '0.68rem' }} tick={{ fill: '#555' }} />
              <RTooltip content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', fontSize: '0.78rem' }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{d.fullName}</div>
                    <div>Custo: {fmt(d.custo)}</div>
                    <div>{d.beneficiarios.toLocaleString('pt-BR')} beneficiários</div>
                    <div>Per capita: {fmt(d.perCapita)}</div>
                  </div>
                );
              }} />
              <Bar dataKey="custo" name="Custo Total" fill="#1565c0" radius={[0, 4, 4, 0]} cursor="pointer"
                onClick={(data) => { if (data?.fullName) setEmpresaSel(data.fullName); }} />
            </BarChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', fontSize: '0.66rem', color: '#bbb', marginTop: 4 }}>
            Clique em uma barra para selecionar a empresa
          </div>
        </div>
      )}

      {/* Metodologia */}
      <div style={{
        padding: '10px 16px', background: '#f8f9fa',
        borderRadius: 8, fontSize: '0.72rem', color: '#666',
      }}>
        <Info size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
        <strong>Metodologia:</strong> A região vem do prestador, não do endereço do beneficiário. Doenças contam só tratamentos ativos, não rastreios. Clique em qualquer gráfico para ver os beneficiários.
      </div>
    </div>
  );
}


// ── Página principal ──────────────────────────────────────────────────────────
// ── AbaEscalonamento — Vigilância precoce por escada (worklist) ──────────────
function AbaEscalonamento({ busca }) {
  const { temNivel } = useAuth();
  const podeReprocessar = temNivel('radar_carteira', 'gestor');
  const [dados, setDados] = useState(null);
  const [eixo, setEixo] = useState('renal');
  const [estagios, setEstagios] = useState([3, 4]);
  const [soEscalando, setSoEscalando] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [expandido, setExpandido] = useState({});
  const [reprocessando, setReprocessando] = useState(false);

  const carregar = useCallback(() => {
    setLoading(true); setErro(null);
    const params = { eixo, estagios: estagios.slice().sort().join(','), so_escalando: soEscalando, limit: 300 };
    if (busca) params.busca = busca;
    return api.get('/radar-carteira/vigilancia-escalonamento', { params })
      .then(r => { setDados(r.data); return r.data; })
      .catch(e => { setDados(null); setErro(e.response?.data?.detail || 'Erro ao carregar'); })
      .finally(() => setLoading(false));
  }, [eixo, estagios, soEscalando, busca]);

  useEffect(() => { carregar(); }, [carregar]);

  const reprocessar = async () => {
    if (reprocessando) return;
    try {
      const r = await api.post('/radar-carteira/refresh-escalonamento');
      if (r.data?.status === 'iniciado' || r.data?.status === 'ja_processando') {
        setReprocessando(true);
        const t0 = dados?.calculado_em;
        const poll = setInterval(async () => {
          const d = await carregar();
          if (d && !d.reprocessando && d.calculado_em !== t0) { clearInterval(poll); setReprocessando(false); }
        }, 5000);
        setTimeout(() => { clearInterval(poll); setReprocessando(false); }, 300000);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro ao reprocessar');
    }
  };

  const fmtBR = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}` : '—');
  const fmtDataHora = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)}` : '—');
  const EIXOS = [
    { id: 'renal', label: 'Renal', tip: 'Doença renal crônica rumo à diálise' },
    { id: 'cardio', label: 'Cardíaco', tip: 'Rumo a evento coronariano (cateterismo/stent/revascularização)' },
    { id: 'diabetes', label: 'Diabético', tip: 'Rumo a complicação (retina/rim/amputação)' },
  ];
  const chip = (ativo, cor = '#00796b') => ({
    fontSize: '0.8rem', fontWeight: 600, padding: '6px 16px', borderRadius: 20, cursor: 'pointer',
    border: '1.5px solid', background: ativo ? cor : '#fff', color: ativo ? '#fff' : '#546e7a',
    borderColor: ativo ? cor : '#cfd8dc',
  });
  const priColor = (p) => p === 'Alta' ? { c: '#b71c1c', bg: '#ffebee' }
    : p === 'Média' ? { c: '#e65100', bg: '#fff3e0' } : { c: '#546e7a', bg: '#eceff1' };
  const toggleEstagio = (n) => setEstagios(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n].sort());
  const bens = dados?.beneficiarios || [];
  const porEst = dados?.por_estagio || {};

  return (
    <div>
      <div style={{ background: 'linear-gradient(135deg,#e0f2f1 0%,#b2dfdb 100%)', borderRadius: 12, padding: '1.2rem 1.4rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Zap size={18} color="#00796b" />
              <span style={{ fontSize: '0.92rem', fontWeight: 700, color: '#00796b' }}>Escalonamento: Vigilância Precoce</span>
            </div>
            <p style={{ fontSize: '0.78rem', color: '#37474f', margin: 0, lineHeight: 1.5, maxWidth: 720 }}>
              Beneficiários subindo a escada de investigação/tratamento numa linha de cuidado, por <strong>prioridade</strong>.
              O sinal é o <strong>padrão ao longo do tempo do próprio beneficiário</strong>, não o custo. Clique no nome para ver a investigação + ação recomendada.
              <em> Watchlist, não diagnóstico.</em>
            </p>
          </div>
          <div style={{ textAlign: 'right', fontSize: '0.7rem', color: '#00695c', whiteSpace: 'nowrap' }}>
            <div style={{ marginBottom: 6 }}><Clock size={11} style={{ verticalAlign: '-1px' }} /> Última atualização: <strong>{fmtDataHora(dados?.calculado_em)}</strong></div>
            {podeReprocessar && (
              <button onClick={reprocessar} disabled={reprocessando}
                style={{ fontSize: '0.72rem', fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: reprocessando ? 'default' : 'pointer', border: '1px solid #00796b', background: reprocessando ? '#b2dfdb' : '#fff', color: '#00796b', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <RefreshCw size={13} style={{ animation: reprocessando ? 'spin 1s linear infinite' : 'none' }} />
                {reprocessando ? 'Processando… (~1 min)' : 'Reprocessar agora'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)', fontWeight: 600 }}>Linha de cuidado:</span>
        {EIXOS.map(e => (
          <Tip key={e.id} text={e.tip}>
            <button onClick={() => setEixo(e.id)} style={chip(eixo === e.id)}>{e.label}</button>
          </Tip>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap', padding: '10px 12px', background: '#f5f7f7', borderRadius: 10 }}>
        <span style={{ fontSize: '0.76rem', color: 'var(--gray-600)', fontWeight: 600 }}>Estágio:</span>
        {[1, 2, 3, 4].map(n => {
          const info = porEst[n] || porEst[String(n)] || { total: 0, escalando: 0 };
          const on = estagios.includes(n);
          return (
            <Tip key={n} text={`${dados?.legenda?.[n] || ''}: ${info.total} ativos, ${info.escalando} acelerando`}>
              <button onClick={() => toggleEstagio(n)} style={{ ...chip(on, n >= 3 ? '#00796b' : '#78909c'), display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                {n} <span style={{ opacity: 0.85, fontSize: '0.7rem' }}>({info.total})</span>
              </button>
            </Tip>
          );
        })}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.76rem', color: 'var(--gray-600)', cursor: 'pointer', marginLeft: 8 }}>
          <input type="checkbox" checked={soEscalando} onChange={e => setSoEscalando(e.target.checked)} />
          Só quem está <strong>acelerando</strong>
          <Tip text={"Mostra só quem teve o padrão se INTENSIFICANDO nos últimos 6 meses (subiu de degrau). É o que separa 'exame de rotina' de 'algo piorando', essencial nos estágios 1-2, que sozinhos são muito volumosos.\n\nLimite: sem o VALOR do exame (só a solicitação), 'acelerando' é o melhor proxy que o dado de conta permite."}>
            <Info size={13} color="#90a4ae" style={{ cursor: 'help' }} />
          </Tip>
        </label>
      </div>

      {loading && <div style={{ padding: 20, color: 'var(--gray-500)', display: 'flex', gap: 8, alignItems: 'center' }}><Loader size={16} /> Carregando…</div>}
      {erro && <div style={{ padding: 20, color: '#e74c3c' }}>{erro}</div>}
      {!loading && !erro && dados && (
        <>
          <div style={{ fontSize: '0.74rem', color: 'var(--gray-500)', marginBottom: 10 }}>
            {busca ? (
              <><strong style={{ color: '#00796b' }}>{dados.total_filtrado}</strong> resultado(s) para "{busca}" em <strong>{dados.linha}</strong> <span style={{ color: 'var(--gray-400)' }}>(busca ignora o filtro de estágio)</span></>
            ) : (
              <><strong style={{ color: '#00796b' }}>{dados.total_filtrado}</strong> na seleção (estágio {estagios.join(', ') || '—'}{soEscalando ? ', acelerando' : ''}) em <strong>{dados.linha}</strong> · {dados.total_ativos} ativos no total da linha</>
            )}
          </div>
          {bens.length === 0 ? (
            <div style={{ padding: 20, color: 'var(--gray-500)' }}>Ninguém nessa seleção. Inclua mais estágios ou desmarque "acelerando".</div>
          ) : bens.map((b, i) => {
            const pc = priColor(b.prioridade);
            const aberto = !!expandido[b.matricula];
            return (
              <div key={i} style={{ border: '1px solid #e0e0e0', borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
                <div onClick={() => setExpandido(p => ({ ...p, [b.matricula]: !p[b.matricula] }))}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer', background: aberto ? '#fafafa' : '#fff' }}>
                  <Tip text={b.prioridade_motivo}>
                    <span style={{ fontSize: '0.66rem', fontWeight: 700, color: pc.c, background: pc.bg, padding: '3px 9px', borderRadius: 6, whiteSpace: 'nowrap' }}>{b.prioridade || '—'}</span>
                  </Tip>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--gray-800)' }}>
                      {b.nome}
                      {b.subiu_recente && <span style={{ fontSize: '0.6rem', color: '#e65100', fontWeight: 700, marginLeft: 6 }}>▲ acelerando</span>}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.empresa} · Mat. {b.matricula}</div>
                  </div>
                  <Tip text={`Degrau ${b.estagio} de 4: ${b.estagio_desc}. ${b.meses_rastreio} mês(es) de exames de rotina antes.`}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 22, height: 22, borderRadius: '50%', background: b.estagio >= 3 ? '#00796b' : '#78909c', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700 }}>{b.estagio}</span>
                      <span style={{ fontSize: '0.72rem', color: 'var(--gray-600)' }}>{b.estagio_desc}</span>
                    </span>
                  </Tip>
                  <div style={{ textAlign: 'right', fontSize: '0.66rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>
                    <div>{b.meses_acompanhamento}m na linha</div>
                    <div>último: {fmtBR(b.ultima_atividade)}</div>
                  </div>
                  {aberto ? <ChevronUp size={16} color="#90a4ae" /> : <ChevronDown size={16} color="#90a4ae" />}
                </div>
                {aberto && (
                  <div style={{ padding: '12px 14px', borderTop: '1px solid #eee', background: '#fafafa' }}>
                    <div style={{ background: '#e0f2f1', border: '1px solid #b2dfdb', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: '0.76rem', color: '#004d40' }}>
                      <strong>Ação recomendada:</strong> {b.acao_recomendada}
                    </div>
                    <PainelInvestigacao matricula={b.matricula} />
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// ── AbaInvestigacaoAtiva — workup diagnóstico sério em quem era saudável ─────
function AbaInvestigacaoAtiva({ busca }) {
  const { temNivel } = useAuth();
  const podeReprocessar = temNivel('radar_carteira', 'gestor');
  const [dados, setDados] = useState(null);
  const [priorLimite, setPriorLimite] = useState(3000);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [expandido, setExpandido] = useState({});
  const [reprocessando, setReprocessando] = useState(false);

  const carregar = useCallback(() => {
    setLoading(true); setErro(null);
    const params = { prior_limite: priorLimite, limit: 400 };
    if (busca) params.busca = busca;
    return api.get('/radar-carteira/investigacao-ativa', { params })
      .then(r => { setDados(r.data); return r.data; })
      .catch(e => { setDados(null); setErro(e.response?.data?.detail || 'Erro ao carregar'); })
      .finally(() => setLoading(false));
  }, [priorLimite, busca]);
  useEffect(() => { carregar(); }, [carregar]);

  const reprocessar = async () => {
    if (reprocessando) return;
    try {
      const r = await api.post('/radar-carteira/refresh-investigacao');
      if (r.data?.status === 'iniciado' || r.data?.status === 'ja_processando') {
        setReprocessando(true);
        const t0 = dados?.calculado_em;
        const poll = setInterval(async () => {
          const d = await carregar();
          if (d && !d.reprocessando && d.calculado_em !== t0) { clearInterval(poll); setReprocessando(false); }
        }, 5000);
        setTimeout(() => { clearInterval(poll); setReprocessando(false); }, 300000);
      }
    } catch (e) { alert(e.response?.data?.detail || 'Erro ao reprocessar'); }
  };

  const fmtBR = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}` : '—');
  const fmtDataHora = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)}` : '—');
  const fmtR = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
  const GRAUS = [
    { v: 3000, label: 'Baixa complexidade (< R$3k)', tip: 'Só quem era de baixo uso antes: o sinal mais afiado ("surpresa").' },
    { v: 8000, label: 'Moderada (< R$8k)', tip: 'Inclui quem tinha uso moderado antes.' },
    { v: 15000, label: 'Ampla (< R$15k)', tip: 'Inclui utilizadores moderados/altos, com mais ruído.' },
    { v: 99999999, label: 'Todos', tip: 'Todos que fizeram biópsia/PAAF/sedação, sem filtro de complexidade (volumoso).' },
  ];
  const chip = (ativo, cor = '#7b1fa2') => ({ fontSize: '0.76rem', fontWeight: 600, padding: '6px 14px', borderRadius: 20, cursor: 'pointer', border: '1.5px solid', background: ativo ? cor : '#fff', color: ativo ? '#fff' : '#546e7a', borderColor: ativo ? cor : '#cfd8dc' });
  const priColor = (p) => p === 'Alta' ? { c: '#b71c1c', bg: '#ffebee' } : { c: '#e65100', bg: '#fff3e0' };
  const bens = dados?.beneficiarios || [];

  return (
    <div>
      <div style={{ background: 'linear-gradient(135deg,#f3e5f5 0%,#e1bee7 100%)', borderRadius: 12, padding: '1.2rem 1.4rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Target size={18} color="#7b1fa2" />
              <span style={{ fontSize: '0.92rem', fontWeight: 700, color: '#7b1fa2' }}>Investigação Ativa</span>
            </div>
            <p style={{ fontSize: '0.78rem', color: '#37474f', margin: 0, lineHeight: 1.5, maxWidth: 720 }}>
              Quem <strong>entrou num workup diagnóstico sério</strong> (biópsia/PAAF, sedação para exame) sendo antes de baixa complexidade: o padrão "pessoa saudável de repente sendo investigada pra algo sério". Clique no nome pra ver a investigação. <em>Detecção operacional pra coordenar o cuidado. Não prevê, não diagnostica.</em>
            </p>
          </div>
          <div style={{ textAlign: 'right', fontSize: '0.7rem', color: '#6a1b9a', whiteSpace: 'nowrap' }}>
            <div style={{ marginBottom: 6 }}><Clock size={11} style={{ verticalAlign: '-1px' }} /> Última atualização: <strong>{fmtDataHora(dados?.calculado_em)}</strong></div>
            {podeReprocessar && (
              <button onClick={reprocessar} disabled={reprocessando} style={{ fontSize: '0.72rem', fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: reprocessando ? 'default' : 'pointer', border: '1px solid #7b1fa2', background: reprocessando ? '#e1bee7' : '#fff', color: '#7b1fa2', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <RefreshCw size={13} style={{ animation: reprocessando ? 'spin 1s linear infinite' : 'none' }} />
                {reprocessando ? 'Processando…' : 'Reprocessar agora'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)', fontWeight: 600 }}>Grau de surpresa:</span>
        {GRAUS.map(g => (
          <Tip key={g.v} text={g.tip}>
            <button onClick={() => setPriorLimite(g.v)} style={chip(priorLimite === g.v)}>{g.label}</button>
          </Tip>
        ))}
      </div>
      <div style={{ fontSize: '0.68rem', color: '#6a1b9a', background: '#f3e5f5', borderRadius: 6, padding: '6px 10px', marginBottom: 14 }}>
        ⚠ Ponto cego honesto: pega workup sério em quem era de <strong>baixa complexidade</strong>. Quem já usava muito o plano antes só aparece abrindo o grau, ao custo de mais ruído.
      </div>

      {loading && <div style={{ padding: 20, color: 'var(--gray-500)', display: 'flex', gap: 8, alignItems: 'center' }}><Loader size={16} /> Carregando…</div>}
      {erro && <div style={{ padding: 20, color: '#e74c3c' }}>{erro}</div>}
      {!loading && !erro && dados && (
        <>
          <div style={{ fontSize: '0.74rem', color: 'var(--gray-500)', marginBottom: 10 }}>
            {busca
              ? (<><strong style={{ color: '#7b1fa2' }}>{dados.total_filtrado}</strong> resultado(s) para "{busca}"</>)
              : (<><strong style={{ color: '#7b1fa2' }}>{dados.total_filtrado}</strong> em workup sério (grau atual) · {dados.total} workups no total da carteira</>)}
          </div>
          {bens.length === 0 ? (
            <div style={{ padding: 20, color: 'var(--gray-500)' }}>Ninguém nessa seleção.</div>
          ) : bens.map((b, i) => {
            const pc = priColor(b.prioridade); const aberto = !!expandido[b.matricula];
            return (
              <div key={i} style={{ border: '1px solid #e0e0e0', borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
                <div onClick={() => setExpandido(p => ({ ...p, [b.matricula]: !p[b.matricula] }))} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer', background: aberto ? '#faf5fb' : '#fff' }}>
                  <span style={{ fontSize: '0.66rem', fontWeight: 700, color: pc.c, background: pc.bg, padding: '3px 9px', borderRadius: 6, whiteSpace: 'nowrap' }}>{b.prioridade}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--gray-800)' }}>{b.nome}</div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.empresa} · Mat. {b.matricula} · custo prévio {fmtR(b.custo_prior)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 260 }}>
                    {(b.sinais || []).map((s, j) => (<span key={j} style={{ fontSize: '0.6rem', fontWeight: 600, color: '#6a1b9a', background: '#f3e5f5', padding: '2px 7px', borderRadius: 5 }}>{s}</span>))}
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '0.66rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>início {fmtBR(b.inicio_workup)}</div>
                  {aberto ? <ChevronUp size={16} color="#90a4ae" /> : <ChevronDown size={16} color="#90a4ae" />}
                </div>
                {aberto && (
                  <div style={{ padding: '12px 14px', borderTop: '1px solid #eee', background: '#faf5fb' }}>
                    <PainelInvestigacao matricula={b.matricula} />
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// ── Painel "?" — Metodologia (fonte única: /radar-carteira/metodologia) ──────
function _MetLinha({ k, v, mono, warn }) {
  if (!v) return null;
  return (
    <div style={{ fontSize: '0.8rem', margin: '3px 0', color: warn ? '#b9770e' : 'var(--gray-600)', lineHeight: 1.4 }}>
      <span style={{ fontWeight: 600, color: 'var(--gray-700)' }}>{k}: </span>
      <span style={mono ? { fontFamily: 'monospace', fontSize: '0.75rem' } : {}}>{v}</span>
    </div>
  );
}

function MetodologiaPanel({ onClose }) {
  const [meta, setMeta] = useState(null);
  const [erro, setErro] = useState(null);
  useEffect(() => {
    api.get('/radar-carteira/metodologia')
      .then(r => setMeta(r.data))
      .catch(e => setErro(e.response?.data?.detail || 'Erro ao carregar metodologia'));
  }, []);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '3vh 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, maxWidth: 920, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--gray-200)', position: 'sticky', top: 0, background: '#fff', borderRadius: '12px 12px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HelpCircle size={20} color="var(--brand, #20a490)" />
            <strong style={{ fontSize: '1.05rem', color: 'var(--gray-800)' }}>Como o Radar rastreia cada coisa</strong>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--gray-500)' }}><X size={20} /></button>
        </div>
        <div style={{ padding: '16px 20px' }}>
          {erro && <div style={{ color: '#e74c3c', fontSize: '0.85rem' }}>{erro}</div>}
          {!meta && !erro && <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--gray-500)' }}><Loader size={16} /> Carregando metodologia…</div>}
          {meta && (
            <>
              <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginBottom: 12 }}>v{meta.versao} · atualizado em {meta.atualizado_em}</div>
              <div style={{ background: '#fff8e1', border: '1px solid #f39c12', borderRadius: 8, padding: '10px 12px', fontSize: '0.82rem', color: '#8a6d1b', marginBottom: 18 }}>{meta.aviso}</div>
              {(meta.secoes || []).map(sec => (
                <div key={sec.id} style={{ marginBottom: 22 }}>
                  <div style={{ fontSize: '0.98rem', fontWeight: 700, color: 'var(--primary)' }}>{sec.titulo}</div>
                  {sec.resumo && <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)', margin: '3px 0 10px' }}>{sec.resumo}</div>}
                  {(sec.itens || []).map((it, i) => (
                    <div key={i} style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, color: 'var(--gray-800)' }}>{it.o_que}</div>
                      <_MetLinha k="Como" v={it.como} />
                      <_MetLinha k="Fonte" v={it.fonte} mono />
                      <_MetLinha k="Regra" v={it.regra} />
                      <_MetLinha k="Limite" v={it.limitacao} warn />
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RadarCarteira() {
  const { user } = useAuth();
  const [showMeta, setShowMeta] = useState(false);
  // ?aba=alertas abre direto numa aba (a ficha do beneficiário leva para os Alertas)
  const [abaAtiva, setAbaAtiva] = useState(() => {
    const pedida = new URLSearchParams(window.location.search).get('aba');
    return ABAS.some((a) => a.id === pedida) ? pedida : 'visao-geral';
  });
  const [statusJob, setStatusJob] = useState(null);
  const [jobExecutando, setJobExecutando] = useState(false);
  const [buscaGlobal, setBuscaGlobal] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');

  // Dados de prospecção
  const [dadosPropec, setDadosPropec] = useState(null);
  const [loadingPropec, setLoadingPropec] = useState(false);
  const [erroPropec, setErroPropec] = useState(null);
  const [filtros, setFiltros] = useState({ oport: 'todos', classificacao: '', limit: 100 });

  const carregarStatus = useCallback(async () => {
    try {
      const r = await api.get('/radar-carteira/status-job');
      setStatusJob(r.data);
    } catch (_) {}
  }, []);

  const carregarProspeccao = useCallback(async () => {
    setLoadingPropec(true); setErroPropec(null);
    try {
      const params = { limit: filtros.limit };
      if (filtros.oport && filtros.oport !== 'todos') params.oportunidade = filtros.oport;
      if (filtros.classificacao) params.classificacao = filtros.classificacao;
      if (buscaAplicada) params.busca = buscaAplicada;
      const r = await api.get('/radar-carteira/lista', { params });
      setDadosPropec(r.data);
    } catch (e) {
      setErroPropec(e.response?.data?.detail || 'Erro ao carregar prospecção');
    } finally { setLoadingPropec(false); }
  }, [filtros, buscaAplicada]);

  useEffect(() => { carregarStatus(); }, [carregarStatus]);
  useEffect(() => { if (abaAtiva === 'prospeccao') carregarProspeccao(); }, [abaAtiva, carregarProspeccao]);

  const executarJob = async () => {
    if (jobExecutando) return;
    setJobExecutando(true);
    try {
      await api.post('/radar-carteira/executar-job');
      setTimeout(carregarStatus, 3000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro ao iniciar job');
    } finally {
      setJobExecutando(false);
    }
  };

  const ult = statusJob?.ultima_execucao;

  return (
    <div style={{ padding: '2rem', maxWidth: 1400, margin: '0 auto' }}>
      {showMeta && <MetodologiaPanel onClose={() => setShowMeta(false)} />}
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--gray-800)', margin: 0 }}>
            Radar da Carteira
          </h1>
          <p style={{ fontSize: '0.85rem', color: 'var(--gray-400)', margin: '4px 0 0' }}>
            Inteligência de carteira: prospecção · risco de sinistralidade · auditoria
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Painel de metodologia — admin only por enquanto */}
          {user?.perfil === 'administrador' && (
            <button
              onClick={() => setShowMeta(true)}
              title="Metodologia: como o Radar rastreia cada coisa (admin)"
              style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--gray-300)', background: '#fff', color: 'var(--gray-600)', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
            >
              <HelpCircle size={15} /> Metodologia
            </button>
          )}
          {/* Status do job */}
          {ult && (
            <Tip text={`Última execução do job IA\nStatus: ${ult.status}\nIniciado: ${ult.iniciado_em?.slice(0,16).replace('T',' ')}\nScores: ${ult.total_scores} | Anomalias: ${ult.total_anomalias}\nFonte: crm.radar_execucoes`}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: ult.status === 'concluido' ? '#e8f5e9' : ult.status === 'executando' ? '#fff8e1' : '#ffebee',
                color: ult.status === 'concluido' ? '#2e7d32' : ult.status === 'executando' ? '#e65100' : '#c62828',
                padding: '6px 12px', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600,
              }}>
                <Cpu size={13} />
                {ult.status === 'concluido' ? `IA atualizada: ${ult.data_ref}` : ult.status === 'executando' ? 'Job em execução...' : `Erro em ${ult.data_ref}`}
              </div>
            </Tip>
          )}
          <Tip text="Dispara o job IA manualmente (apenas administradores)\nProcessa todos os beneficiários via GPT-4o e salva em crm.radar_scores e crm.radar_anomalias">
            <button onClick={executarJob} disabled={jobExecutando}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: 'var(--primary)', color: '#fff', fontWeight: 600, fontSize: '0.82rem',
              }}>
              <Cpu size={14} className={jobExecutando ? 'spin' : ''} />
              {jobExecutando ? 'Iniciando...' : 'Executar Job'}
            </button>
          </Tip>
        </div>
      </div>

      {/* Busca global */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem',
        background: '#fff', borderRadius: 10, padding: '6px 14px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e0e0e0',
        maxWidth: 480,
      }}>
        <Search size={16} color="var(--gray-400)" />
        <input
          type="text"
          placeholder="Buscar por matrícula, nome ou código de prestador..."
          value={buscaGlobal}
          onChange={(e) => setBuscaGlobal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') setBuscaAplicada(buscaGlobal.trim()); }}
          style={{
            flex: 1, border: 'none', outline: 'none', fontSize: '0.82rem',
            color: 'var(--gray-700)', background: 'transparent',
          }}
        />
        {buscaGlobal && (
          <button onClick={() => { setBuscaGlobal(''); setBuscaAplicada(''); }} style={{
            border: 'none', background: 'none', cursor: 'pointer', color: 'var(--gray-400)',
            fontSize: '0.9rem', padding: 0, lineHeight: 1,
          }}>×</button>
        )}
        <button onClick={() => setBuscaAplicada(buscaGlobal.trim())} style={{
          padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
          background: 'var(--primary)', color: '#fff', fontWeight: 600, fontSize: '0.72rem',
        }}>Buscar</button>
      </div>

      {/* Abas */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: '1.5rem',
        background: '#f5f5f5', borderRadius: 12, padding: 4,
        width: 'fit-content',
      }}>
        {ABAS.map(({ id, label, icon: Icon, tip }) => (
          <Tip key={id} text={tip}>
            <button
              onClick={() => setAbaAtiva(id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 9, border: 'none', cursor: 'pointer',
                background: abaAtiva === id ? '#fff' : 'transparent',
                color: abaAtiva === id ? 'var(--primary)' : 'var(--gray-500)',
                fontWeight: abaAtiva === id ? 700 : 500,
                fontSize: '0.82rem',
                boxShadow: abaAtiva === id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              <Icon size={15} />
              {label}
            </button>
          </Tip>
        ))}
      </div>

      {/* Conteúdo da aba */}
      {abaAtiva === 'visao-geral' && <AbaVisaoGeral />}
      {abaAtiva === 'alertas' && <AbaAlertas busca={buscaAplicada} />}
      {abaAtiva === 'prospeccao' && (
        <AbaProspeccao
          dados={dadosPropec}
          loading={loadingPropec}
          erro={erroPropec}
          onRefresh={carregarProspeccao}
          filtros={filtros}
          setFiltros={setFiltros}
        />
      )}
      {abaAtiva === 'sinistralidade' && <AbaSinistralidade statusJob={statusJob} busca={buscaAplicada} />}
      {abaAtiva === 'auditoria' && <AbaAuditoria statusJob={statusJob} busca={buscaAplicada} />}
      {abaAtiva === 'malha' && <AbaMalha />}
      {abaAtiva === 'curva_doenca' && <AbaCurvaDoenca busca={buscaAplicada} />}
      {abaAtiva === 'escalonamento' && <AbaEscalonamento busca={buscaAplicada} />}
      {abaAtiva === 'investigacao' && <AbaInvestigacaoAtiva busca={buscaAplicada} />}
      {abaAtiva === 'saude_corporativa' && <AbaSaudeCorporativa />}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
